// RK Product Customiser. Inline PDP root.
//
// Multi-placement editor. Owns:
//   - spots: list of saved placements the customer has finished configuring.
//   - draft: the placement currently being edited, OR null when no editor
//            is open (customer just hit "Add another" or removed the last
//            spot. They need to choose to start a new one).
//
// Pricing + cart-payload logic is in `./pricing.ts` (single source of truth).
// Section composition + sub-flow is in `./sections/PlacementSection.tsx`.

import * as React from "react";
import * as ReactDOM from "react-dom";
import {
  fetchConfig,
  fetchCart,
  addItemsToCart,
  fetchVariantPriceCents,
  fetchProductJson,
  type ProductJsonShape,
  saveDesign,
  saveDraft,
  lookupDraft,
  trackDesignStarted,
  uploadedFileUrl,
} from "./api";
import { resolveVariantId } from "./util/variantResolver";
import {
  isValidGroupId,
  mintGroupId,
  reuseOrMintGroupId,
} from "./util/groupId";
import { atcEditorBlock } from "./util/atcGate";
import { guardRestoredState } from "./util/resumeState";
import { easedScrollTo as sharedEasedScrollTo } from "./util/scroll";
import { assignCustomDiscounts, memberQualifies } from "./util/customDiscount";
import { buildPropagatedProps, buildMemberCartProps } from "./util/bundleMemberLine";
import type { BundleMember, Config, CustomField } from "./types";
import { flowCopy } from "./copy";
import { designUpsellFor, flowKindFor } from "./flow";
import {
  PlacementSection,
  emptyDraft,
  isDraftComplete,
  type EmbroideryDraft,
} from "./sections/PlacementSection";
import { MATCH_DESIGN_CODE } from "./components/ThreadPicker";
import { ReuseSection } from "./sections/ReuseSection";
import { ExamplesStrip } from "./sections/ExamplesStrip";
import { activeOccasion } from "./occasions";
import { FinishSection } from "./sections/FinishSection";
import { SleeveCrossSellSection } from "./sections/SleeveCrossSellSection";
import { eligibleOrderUpsells } from "./sections/OrderAddOnsSection";
import { CustomFieldsSection } from "./sections/CustomFieldsSection";
import { BundlePreviewSection } from "./sections/BundlePreviewSection";
import { SecondItemCard } from "./sections/SecondItemCard";
import { LeadHelper, type LeadHelperKind } from "./sections/LeadHelper";
import { WorkflowFooter } from "./sections/WorkflowFooter";
import { PreDesignerSummary } from "./sections/PreDesignerSummary";
import { emitAnalytics, emitGa4AddToCart } from "./analytics";
import { SavedSpotTile } from "./components/SavedSpotTile";
import { PriceBreakdown } from "./components/PriceBreakdown";
import { PreviewCanvas } from "./components/PreviewCanvas";
import { OnGarmentPreview } from "./components/OnGarmentPreview";
import {
  leanCartProperties,
  lockedSlugsFor,
  priorLogosFrom,
  propertiesFor,
  totalCentsFor,
  type ApprovalChoice,
  type Spot,
} from "./pricing";
import { decodeReusableDesigns, type ReusableDesign } from "./reuse";
import { stagesFor, stageOfSlug, unstagedSlugs, type Stage } from "./stages";
import { CustomFieldRenderer } from "./components/fields/CustomFieldRenderer";
import { evaluateCondition } from "./util/conditions";
import { VariantAxisPicker } from "./components/VariantAxisPicker";

// Multi-buy bundle promo KILL SWITCH. RE-ENABLED 2026-07-02 with the
// rebuilt garment-only fixed-amount Function (no line splitting, labour
// protected) - node 1375051088035.
// REMOVED 2026-07-09 (Oscar): discount deactivated (node now EXPIRED),
// this flag flipped false (kills the builder banner/strike-throughs/
// savings strip), theme drawer set multibuy:false, and the
// pet-multibuy-banner snippet already renders nothing (removed
// 2026-07-03) - so no surface advertises a dead offer. To relaunch:
// flip true + reactivate the node (scripts/relaunch-multibuy.ts) +
// set multibuy:true in rkc-cart-drawer.liquid.
const MULTIBUY_ENABLED = false;

// Stuck-helper "need a hand? email us" dialog KILL SWITCH. Disabled 2026-07-04:
// the auto exit-intent + dwell pop-up was interrupting shoppers mid-design and
// was suspected of hurting conversion. Off = the customiser just works, no
// pop-up. The dialog code + triggers are kept intact so it can be re-enabled
// (flip true) if we want to revisit it as a gentler, opt-in prompt later.
const STUCK_HELPER_ENABLED = false;

// 2026-07-03: sold-out back-order (born as 15%-off "pre-order"). When a
// customer designs on an OUT-OF-STOCK size, they can add it as a back-order
// (the garment portion rides on the hidden in-stock exact-price product;
// the real variant's inventory is never touched). 2026-07-25: Oscar's call -
// FULL price, no discount, honest ship-date note stays. KILL SWITCH: the
// hidden rk-custom-preorder product must also be ACTIVE + published, or
// every back-order add hard-fails to the sold-out message.
// 2026-08-10: Oscar's call - back-orders OFF ("this needs to be stopped now").
// The hidden Custom Back-order product (9165309477027) is also set to DRAFT,
// so even stale bundles in open sessions hard-fail to the sold-out message.
// Re-enabling needs BOTH: this flag true AND the product back to ACTIVE.
const PREORDER_ENABLED = false;
// Honest, supplier-agnostic restock ETA shown in the pre-order UI + carried
// on the line for the customer + team. Used only when we DON'T have a real
// supplier delivery date for the specific sold-out size.
const PREORDER_ETA_COPY = "usually about 2 to 3 weeks";

// Format a supplier next-delivery ISO date into friendly, honest copy, e.g.
// "around 10 August". Returns null for missing / unparseable / past dates so
// callers fall back to PREORDER_ETA_COPY. Never over-promises a weekday.
function formatPreorderEta(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms);
  // Only surface dates comfortably in the future (>2 days out); a date that has
  // effectively passed would read as wrong.
  if (ms < Date.now() + 2 * 24 * 60 * 60 * 1000) return null;
  const now = new Date();
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() === now.getFullYear()
      ? { day: "numeric", month: "long" }
      : { day: "numeric", month: "long", year: "numeric" };
  try {
    return `around ${new Intl.DateTimeFormat("en-AU", opts).format(d)}`;
  } catch {
    return null;
  }
}

// Abandoned-design recovery (Klaviyo) guest capture is BUILT but not launched.
// The plumbing is now wired end to end (server-side BuilderDraft saves via
// /api/save-draft, the ?rk_draft resume deep link, and the enriched
// "Started Custom Design" event carrying resume_url + thumb) - but the
// Klaviyo recovery FLOW itself isn't live yet, so the guest "save your
// design" card would still promise an email that never sends. Flip this
// true once the flow is on; everything downstream is ready.
const KLAVIYO_RECOVERY_ENABLED = false;

export type MountContext = {
  workerUrl: string;
  productGid: string;
  productHandle: string;
  shopDomain: string;
  currency: string;
  /** Logged-in customer's email when the theme can resolve it. Used for
   *  cross-order digitisation memory: if the same hash is uploaded twice
   *  by the same email, the $30 fee is waived the second time. Empty for
   *  guests; the fee always applies. */
  customerEmail: string;
  variantId: string | null;
  variantSize: string | null;
  garmentColourName: string | null;
  garmentColourHex: string | null;
  missingOption: string | null;
};

let __spotIdSeq = 0;
const nextSpotId = () => `s-${++__spotIdSeq}-${Math.random().toString(36).slice(2, 7)}`;

// Scroll-container discovery + the eased scroll itself now live in
// util/scroll.ts only. App.tsx used to carry local duplicates of both, and
// the local scroll was missing every mobile guard (see easedScrollTo below).

// Phase 35e: sessionStorage key. The customiser remounts on variant
// change (see embed.ts variant:change listener) so without persistence
// the customer loses their spots whenever they pick a different size or
// colour. We key by productGid (same across all variants of one product)
// so the spots survive the remount but DON'T leak across products.
function persistKey(productGid: string): string {
  return `rk-pc:state:${productGid}`;
}

type PersistedState = {
  spots: Spot[];
  approvalChoice: ApprovalChoice;
  approvalAddonId: string | null;
  customerNotes: string;
  /** Phase 37: order-level upsell IDs the customer has ticked
   *  (Care kit, Gift box, Rush production, etc.). */
  orderAddOnIds: string[];
  /** Phase 72: admin-defined CustomField values, keyed by field.slug.
   *  Survives navigation / variant switches just like spots. */
  fieldValues?: Record<string, string | string[]>;
  /** Phase 75: which optional bundle members the customer has ticked.
   *  Persisted as an array (sessionStorage can't roundtrip a Set). */
  selectedBundleIds?: string[];
  /** Phase 81: customer's variant pick per bundle member (numeric
   *  variant id as string). Survives variant-driven remounts so the
   *  customer doesn't have to re-pick after switching the PDP's size. */
  bundleMemberVariantPicks?: Record<string, string>;
  /** The variantId that was active when the customer last touched the
   *  flow. We compare this to ctx.variantId on mount; mismatch ⇒ surface
   *  the "you changed your size/colour" banner so the customer sees
   *  their work is still there. */
  lastVariantId: string | null;
  /** 2026-07-02: the OPEN (unsaved) draft. Previously only saved spots
   *  survived a refresh/tab-restore, so a customer half-way through their
   *  first spot (photo uploaded, name typed, threads picked) came back to
   *  an empty customiser. Uploads ride as staging keys (strings), so the
   *  whole draft is JSON-serialisable. Best-effort: null when the editor
   *  is closed. */
  draft?: EmbroideryDraft | null;
  /** Abandonment recovery: the design token (`rk-<base36>-<base36>`),
   *  minted at the FIRST spot save and reused by every re-save, the
   *  server-side BuilderDraft id, and finally the cart line's
   *  `_rk_group_id` at add-to-cart. Null until a spot is saved. */
  groupId?: string | null;
};

// Who supplied the email backing the server draft sync.
type DraftSyncSource = "guest_card" | "logged_in";

// Abandonment recovery: how long an edit burst can run before the
// server-side BuilderDraft re-save fires (dirty-flag debounce).
const DRAFT_SYNC_DEBOUNCE_MS = 12000;

// C7: how long a design survives in localStorage. sessionStorage is wiped
// when the tab closes, so on its own a shopper who quits the browser (or
// whose mobile tab is discarded overnight) loses their whole design. We
// ALSO mirror the state into localStorage so it survives a full restart,
// but only honour it for 7 days so a stale draft doesn't ambush a returning
// customer weeks later.
const PERSIST_TTL_MS = 7 * 24 * 60 * 60 * 1000;

type PersistedEnvelope = PersistedState & { savedAt?: number };

function loadPersisted(productGid: string): PersistedState | null {
  if (typeof window === "undefined") return null;
  const key = persistKey(productGid);
  // Prefer the session copy — it's the freshest and survives the
  // variant-driven remounts within this tab.
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw) return JSON.parse(raw) as PersistedState;
  } catch {
    // fall through to the localStorage copy
  }
  // C7: fall back to the longer-lived localStorage copy so the design
  // survives a browser restart. Honoured only within the TTL; a stale
  // entry is dropped on read.
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const env = JSON.parse(raw) as PersistedEnvelope;
    if (
      typeof env.savedAt === "number" &&
      Date.now() - env.savedAt > PERSIST_TTL_MS
    ) {
      window.localStorage.removeItem(key);
      return null;
    }
    return env;
  } catch {
    return null;
  }
}

function savePersisted(productGid: string, state: PersistedState): void {
  if (typeof window === "undefined") return;
  const key = persistKey(productGid);
  try {
    window.sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Quota / private mode. Non-fatal — we just lose persistence.
  }
  // C7: mirror to localStorage with a timestamp so the draft can be
  // recovered after the tab/browser is closed (see loadPersisted).
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({ ...state, savedAt: Date.now() } satisfies PersistedEnvelope),
    );
  } catch {
    // Quota / private mode. Non-fatal.
  }
}

// C7: clear BOTH the session + local persistence copies. Called once the
// design is safely in the cart / the customer starts over, so a recovered
// draft never re-hydrates on top of a fresh flow.
function clearPersisted(productGid: string): void {
  if (typeof window === "undefined") return;
  const key = persistKey(productGid);
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // non-fatal
  }
  try {
    window.localStorage.removeItem(key);
  } catch {
    // non-fatal
  }
}

// ── Recent designs ─────────────────────────────────────────────────────
// A localStorage-backed list of designs the customer has added to their
// cart. Kept INDEPENDENTLY of /cart.js so "reuse a previous design" works
// even when a cart app (bundling / merge / product-options) rewrites the
// line-item properties the cart decoder would otherwise read. Survives
// across product pages (localStorage, not sessionStorage).
const RECENT_KEY = "rk-pc:recent-designs";
const RECENT_MAX = 12;

type RecentDesign = {
  sourceLabel: string;
  draft: EmbroideryDraft;
  ts: number;
};

function loadRecentDesigns(): RecentDesign[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RecentDesign[]) : [];
  } catch {
    return [];
  }
}

function saveRecentDesigns(
  drafts: EmbroideryDraft[],
  sourceLabel: string,
): void {
  if (typeof window === "undefined" || drafts.length === 0) return;
  try {
    const ts = Date.now();
    const fresh: RecentDesign[] = drafts.map((d) => ({
      sourceLabel,
      draft: d,
      ts,
    }));
    const merged = [...fresh, ...loadRecentDesigns()].slice(0, RECENT_MAX);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(merged));
  } catch {
    // private mode / quota — non-fatal
  }
}

// ── Reorder deep link (2026-07-02 CRO 2.5) ────────────────────────────
// Klaviyo post-delivery emails link back to a PDP with
//   ?rk_reuse=<orderName>&e=<email>
// so the "From a past order" flow opens pre-filled and fires the lookup
// without the customer re-typing anything ("your design is digitised and
// on file, put it on a crewneck"). `e` accepts the plain address
// (Klaviyo `{{ person.email|urlencode }}`) or a base64url-encoded one;
// anything that doesn't decode to an address is ignored and the customer
// just types their email. Both params are stripped from the URL
// immediately so the address never rides into analytics page_location
// captures and a refresh doesn't re-fire the lookup.
export type ReuseDeepLink = { orderName: string; email: string | null };

// ── "Customise it" deep link (2026-07-22) ─────────────────────────────
// The Father's Day collection hero ("One gift, sorted.") links to the bundle
// PDP with ?rk_open=1 so its button lands the customer IN the builder rather
// than at the top of the product page.
//
// WHY A PARAM AND NOT A #HASH. The theme app embed ships the host hidden and
// off-screen (`visibility:hidden;position:absolute;left:-9999px`) and a boot
// script then MOVES it into the product form, just above add-to-cart, before
// revealing it. A native hash scroll fires once on load, against an element
// that at that moment is neither visible nor in its final position, so it
// lands nowhere useful. Scrolling ourselves after mount is the only reliable
// option.
//
// Stripped from the URL immediately, same contract as the reorder deep link
// below, so a refresh does not re-fire it and it never rides into an
// analytics page_location capture.
function readOpenDeepLink(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("rk_open")) return false;
    params.delete("rk_open");
    const qs = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
    );
    return true;
  } catch {
    return false;
  }
}

function readReuseDeepLink(): ReuseDeepLink | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const orderName = (params.get("rk_reuse") ?? "").trim();
    if (!orderName) return null;
    const rawEmail = (params.get("e") ?? "").trim();
    let email: string | null = null;
    if (rawEmail.includes("@")) {
      email = rawEmail;
    } else if (rawEmail) {
      try {
        const decoded = window.atob(
          rawEmail.replace(/-/g, "+").replace(/_/g, "/"),
        );
        if (decoded.includes("@")) email = decoded.trim();
      } catch {
        // Not base64 (an opaque hash we can't reverse). Pre-fill the
        // order number only.
      }
    }
    params.delete("rk_reuse");
    params.delete("e");
    const qs = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
    );
    return { orderName, email };
  } catch {
    return null;
  }
}

// ── Resume deep link (abandonment recovery) ───────────────────────────
// Klaviyo recovery emails link back to the PDP with
//   ?rk_draft=<draftId>&e=<email>
// so the server-saved BuilderDraft can rebuild the design on any device,
// not just the browser whose localStorage holds it. Same contract as
// readReuseDeepLink above: `e` accepts a plain address or a
// base64url-encoded one, and both params are stripped via replaceState
// BEFORE any analytics fire so the address never rides into
// page_location captures (and a refresh doesn't re-fire the lookup).
export type DraftDeepLink = { draftId: string; email: string | null };

function readDraftDeepLink(): DraftDeepLink | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const draftId = (params.get("rk_draft") ?? "").trim();
    if (!draftId) return null;
    const rawEmail = (params.get("e") ?? "").trim();
    let email: string | null = null;
    if (rawEmail.includes("@")) {
      email = rawEmail;
    } else if (rawEmail) {
      try {
        const decoded = window.atob(
          rawEmail.replace(/-/g, "+").replace(/_/g, "/"),
        );
        if (decoded.includes("@")) email = decoded.trim();
      } catch {
        // Not base64 - the lookup can't run without an email; the params
        // are still stripped below so the URL comes out clean.
      }
    }
    params.delete("rk_draft");
    params.delete("e");
    const qs = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
    );
    if (!isValidGroupId(draftId)) return null;
    return { draftId, email };
  } catch {
    return null;
  }
}

// Loose identity for a design, used to de-duplicate the cart-decoded list
// against the localStorage recents so the same design isn't offered twice.
function designSignature(d: EmbroideryDraft): string {
  return [
    d.mode ?? "",
    d.placementSlug ?? "",
    d.uploadStagingKey ?? "",
    (d.text ?? "").trim().toLowerCase(),
    d.fontId ?? "",
  ].join("|");
}

export function App({ ctx }: { ctx: MountContext }) {
  const [config, setConfig] = React.useState<Config | null>(null);
  // Config-load failure only. Fatal (there is nothing to show), so the
  // top-level render swaps the whole customiser for the fail-open banner.
  const [err, setErr] = React.useState<string | null>(null);
  // Add-to-cart failure. NOT fatal: the design is intact and the customer
  // can just try again, so this renders inline by the button instead of
  // wiping the whole customiser (which stranded a completed design behind
  // a dead-end "briefly unavailable" banner).
  const [atcError, setAtcError] = React.useState<string | null>(null);

  // Hydrate from sessionStorage so spots survive variant-driven remounts.
  const persisted = React.useMemo(
    () => loadPersisted(ctx.productGid),
    [ctx.productGid],
  );

  // Saved spots + the currently-active draft. `draft === null` means no
  // editor is open and the customer has at least one saved spot. The
  // section shows the spots list + "+ Add another spot" button only.
  const [spots, setSpots] = React.useState<Spot[]>(
    () => persisted?.spots ?? [],
  );
  // Phase B2: the editor no longer auto-opens on load — the Reuse section
  // leads the flow. A draft is created only when the customer picks
  // "Start fresh" or reuses a previous design.
  // 2026-07-02: EXCEPT when a half-built draft survived in sessionStorage
  // (refresh / mobile tab discard) - restore it so no work is ever lost.
  const [draft, setDraft] = React.useState<EmbroideryDraft | null>(
    () => persisted?.draft ?? null,
  );

  // Abandonment recovery: the design token (`rk-<base36>-<base36>`).
  // Minted at the FIRST spot save (previously minted at add-to-cart),
  // persisted in the envelope so re-saves + variant remounts reuse it,
  // used as the BuilderDraft id server-side, and finally emitted on the
  // cart line as `_rk_group_id`. Null until a spot is saved; add-to-cart
  // falls back to minting on the spot, exactly as it always did.
  const [designGroupId, setDesignGroupId] = React.useState<string | null>(
    () => {
      const g = persisted?.groupId;
      return isValidGroupId(g) ? g : null;
    },
  );

  // Phase 35e: detect a variant change since the last visit. Shown as a
  // soft banner so the customer knows their config is still good.
  const [variantSwitched, setVariantSwitched] = React.useState<boolean>(() => {
    if (!persisted) return false;
    if (!persisted.lastVariantId || !ctx.variantId) return false;
    return persisted.lastVariantId !== ctx.variantId;
  });
  // When the customer hits Edit on a saved tile, we move the spot OUT of
  // `spots` and INTO `draft`, remembering its id so saving updates in
  // place (rather than appending a duplicate).
  const [editingSpotId, setEditingSpotId] = React.useState<string | null>(null);
  // Garment stages (see ./stages.ts). Non-null only on multi-garment templates
  // like the Father's Day Gift Box. Holds the stage whose CTA opened the
  // editor, so the placement picker can be scoped to that garment.
  const [activeStageKey, setActiveStageKey] = React.useState<string | null>(
    null,
  );

  // Approval upsell. Customer must pick before add-to-cart unlocks. Reset
  // to null any time the spots list changes so an old answer doesn't ride
  // along after the customer reworks their design.
  // Phase B1: default to "skip" — "Skip, go straight in" is pre-selected so
  // add-to-cart isn't gated on an approval click. Approval is now the last
  // section; the customer upgrades to "Approve before stitching" only if
  // they want to.
  const [approvalChoice, setApprovalChoice] = React.useState<ApprovalChoice>(
    () => persisted?.approvalChoice ?? "skip",
  );
  // Phase 22b: id of an order-level upsell that REPLACES the standard
  // approval step (e.g. "Alternate colourway proof"). When set, the
  // upsell's price applies in lieu of the standard approval fee, and
  // approvalChoice is auto-set to "approve" since the customer is opting
  // into approval-with-extras.
  const [approvalAddonId, setApprovalAddonId] = React.useState<string | null>(
    () => persisted?.approvalAddonId ?? null,
  );

  // Optional free-text notes for the embroidery team. Not required to add
  // to cart; surfaces on the cart line when non-empty.
  const [customerNotes, setCustomerNotes] = React.useState<string>(
    () => persisted?.customerNotes ?? "",
  );

  // Phase 37: order-level add-on upsell IDs the customer has ticked
  // (Care kit, Gift box, Rush production). Stacked into pricing +
  // emitted on the cart line.
  const [orderAddOnIds, setOrderAddOnIds] = React.useState<string[]>(
    () => persisted?.orderAddOnIds ?? [],
  );
  const toggleOrderAddOn = (id: string) => {
    setOrderAddOnIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // Phase 72: admin-defined custom-field values. Single Map keyed by
  // field.slug. Persisted alongside spots/notes/etc. so a page reload
  // doesn't drop the customer's typed text or picked options.
  const [fieldValues, setFieldValues] = React.useState<
    Record<string, string | string[]>
  >(() => {
    const p = persisted?.fieldValues;
    return p && typeof p === "object" ? p : {};
  });
  const setFieldValue = (slug: string, value: string | string[]) => {
    setFieldValues((prev) => ({ ...prev, [slug]: value }));
  };

  // Phase 83: seed admin-set defaults once config resolves. Only fills
  // slugs with NO entry in the map - a persisted value (or a value the
  // customer explicitly cleared, which persists as "") always wins, so
  // reloading never stomps customer input. Checkbox-group defaults are
  // comma-separated in the admin ("a,b") and split to the string[]
  // shape the renderer + pricing expect.
  React.useEffect(() => {
    if (!config) return;
    const fields = config.customFields ?? [];
    if (fields.length === 0) return;
    setFieldValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const f of fields) {
        if (!f.defaultValue) continue;
        if (f.slug in next) continue;
        next[f.slug] =
          f.fieldType === "checkboxes"
            ? f.defaultValue.split(",").map((s) => s.trim()).filter(Boolean)
            : f.defaultValue;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [config]);

  // Phase 73: cache the product's full /products/<handle>.js so the
  // variant-binding resolver can intersect picked variant_axis field
  // values against the variant grid at add-to-cart. Fetched once per
  // mount; null until the request returns (in which case the resolver
  // falls back to ctx.variantId).
  const [productJson, setProductJson] = React.useState<ProductJsonShape | null>(
    null,
  );
  React.useEffect(() => {
    if (!ctx.productHandle) return;
    let cancelled = false;
    fetchProductJson(ctx.productHandle).then((j) => {
      if (!cancelled) setProductJson(j);
    });
    return () => {
      cancelled = true;
    };
  }, [ctx.productHandle]);

  // Phase 75: bundle member selection state + live prices.
  // - selectedBundleIds: which optional members the customer has
  //   ticked. Mandatory members (optional=false) aren't in this set;
  //   they're always treated as included. Persisted alongside
  //   spots/notes so a variant switch doesn't drop the picks.
  // - bundleMemberPrices: per-member variant price in cents, fetched
  //   via /products/<handle>.js (one fetch per unique handle). Null
  //   slots mean "not loaded yet"; the UI shows a quiet placeholder.
  const [selectedBundleIds, setSelectedBundleIds] = React.useState<Set<string>>(
    () => {
      const p = persisted?.selectedBundleIds;
      if (Array.isArray(p)) return new Set(p);
      return new Set();
    },
  );
  const toggleBundleMember = (memberId: string) => {
    setSelectedBundleIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
        // Phase 81: untick also drops the variant pick + collapses
        // any expand on this row, so re-tick later starts fresh.
        setBundleMemberVariantPicks((picks) => {
          if (!(memberId in picks)) return picks;
          const out = { ...picks };
          delete out[memberId];
          return out;
        });
        setExpandedBundleRowId((cur) => (cur === memberId ? null : cur));
      } else {
        next.add(memberId);
        // Phase 81: auto-expand the picker when ticking a member that
        // has more than one variant AND no pick yet. Single-variant
        // members skip the picker entirely (handled in the section).
        const data = bundleMemberProductData[memberId];
        const hasMultipleVariants = (data?.variants?.length ?? 0) > 1;
        const alreadyPicked = !!bundleMemberVariantPicks[memberId];
        if (hasMultipleVariants && !alreadyPicked) {
          setExpandedBundleRowId(memberId);
        }
      }
      return next;
    });
  };
  const [bundleMemberPrices, setBundleMemberPrices] = React.useState<
    Record<string, number | null>
  >({});
  // Phase 81: keep the full /products/<handle>.js shape per member so
  // the inline variant picker can render every variant + axis name.
  // Was previously thrown away after extracting the first variant's
  // price. Same fetch as bundleMemberPrices - extends, doesn't add.
  const [bundleMemberProductData, setBundleMemberProductData] = React.useState<
    Record<string, ProductJsonShape | null>
  >({});
  // Phase 81: customer's variant pick per bundle member (numeric
  // variant id as string, matching the rest of the codebase). When
  // unset, ATC falls back to admin-picked variantGid OR first variant.
  const [bundleMemberVariantPicks, setBundleMemberVariantPicks] = React.useState<
    Record<string, string>
  >(() => {
    const p = persisted?.bundleMemberVariantPicks;
    return p && typeof p === "object" ? p : {};
  });
  const setBundleMemberVariantPick = (memberId: string, variantId: string) => {
    setBundleMemberVariantPicks((prev) => ({ ...prev, [memberId]: variantId }));
  };
  // 2026-07-02: post-ATC "complete the set" card state. lastAtcRef stashes
  // the group id + propagated design props of the most recent add-to-cart
  // so a member added from the card joins the SAME design group;
  // quickAddedIds advances the card once a member is taken.
  const lastAtcRef = React.useRef<{
    groupId: string;
    propagatedProps: Record<string, string>;
  } | null>(null);
  const [quickAddedIds, setQuickAddedIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [quickAddBusy, setQuickAddBusy] = React.useState(false);

  // 2026-07-03: stuck-helper lead capture. Opens on exit-intent (desktop
  // pointer leaving via the top) OR dwell-without-progress, once per session,
  // and never once the design is in the cart. kind "preorder" is opened
  // separately from the sold-out variant panel.
  const [leadHelperKind, setLeadHelperKind] =
    React.useState<LeadHelperKind | null>(null);
  const helperShownRef = React.useRef(false);
  const HELPER_SESSION_KEY = "rk-pc:helper-shown";
  const offerHelper = React.useCallback(() => {
    if (helperShownRef.current) return;
    try {
      if (window.sessionStorage.getItem(HELPER_SESSION_KEY)) return;
    } catch {
      /* private mode */
    }
    helperShownRef.current = true;
    try {
      window.sessionStorage.setItem(HELPER_SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
    setLeadHelperKind("question");
    emitAnalytics("stuck_helper_shown", {
      productGid: ctx.productGid,
      productHandle: ctx.productHandle,
    });
  }, [ctx.productGid, ctx.productHandle]);
  // Latest progress snapshot for the dwell timer (avoids resetting it on
  // every keystroke). "Progressing" = at least one spot saved OR already
  // added to cart.
  const helperProgressRef = React.useRef({ spotsLen: 0, added: false });
  // Phase 81: which bundle row is expanded (variant picker visible).
  // Only one at a time so mobile screens don't drown. Defaults to null
  // (all collapsed); auto-opens on tick when variants exist + no pick.
  const [expandedBundleRowId, setExpandedBundleRowId] = React.useState<string | null>(null);
  const toggleExpandBundleRow = (memberId: string) => {
    setExpandedBundleRowId((cur) => (cur === memberId ? null : memberId));
  };
  React.useEffect(() => {
    const members = config?.bundleMembers ?? [];
    if (members.length === 0) return;
    let cancelled = false;
    // Parallel fetches, one per unique productHandle. Each member's
    // price = its variantGid's price if set + present, else the
    // product's first variant's price.
    (async () => {
      const results = await Promise.all(
        members.map(async (m) => {
          if (!m.productHandle) return [m.id, null, null] as const;
          const j = await fetchProductJson(m.productHandle);
          if (!j?.variants || j.variants.length === 0) {
            return [m.id, null, j ?? null] as const;
          }
          let v: { id: number | string; price?: number } | undefined;
          if (m.variantGid) {
            const numeric = m.variantGid.split("/").pop();
            v = j.variants.find((x) => String(x.id) === String(numeric));
          }
          if (!v) v = j.variants[0];
          const price = typeof v?.price === "number" ? v.price : null;
          return [m.id, price, j] as const;
        }),
      );
      if (cancelled) return;
      const priceMap: Record<string, number | null> = {};
      const dataMap: Record<string, ProductJsonShape | null> = {};
      for (const [id, price, data] of results) {
        priceMap[id] = price;
        dataMap[id] = data;
      }
      setBundleMemberPrices(priceMap);
      setBundleMemberProductData(dataMap);
    })();
    return () => {
      cancelled = true;
    };
  }, [config]);

  // Phase 88: how many qualifying customiser units the customer already
  // has in /cart.js. Used by the unified multi-buy discount preview to
  // carry the tier across multiple add-to-cart sessions (so adding a 2nd
  // custom item shows -15% even if the 1st was added a screen ago).
  // Refreshed on mount + after every add-to-cart via refreshCart. Counted
  // by the `_rk_group_id` line-item property the builder writes (handle-
  // independent, so it works for every customiser product) excluding the
  // surcharge fee line.
  const [existingCustomCountInCart, setExistingCustomCountInCart] = React.useState<number>(0);
  // Companion to the count: the per-unit garment prices of those existing
  // units, so the multi-buy preview ranks existing + new units globally by
  // price exactly like the checkout Function (2026-07-02 review finding).
  const [existingCustomUnitPricesCents, setExistingCustomUnitPricesCents] =
    React.useState<number[]>([]);

  // Threshold unlock line: what's already in the cart (line-discounted
  // subtotal, cents) so the foot can project "cart + this item" against the
  // $90 free-shipping / $250 free-socks thresholds. Same /cart.js piggyback
  // as the count above; refreshed on mount + after every add-to-cart.
  const [cartSubtotalCents, setCartSubtotalCents] = React.useState<number>(0);

  // Phase B2: top-level accordion. One section body open at a time;
  // `openSection` holds its id (null = all collapsed). Fresh visitors
  // 2026-07-02: first-time visitors land straight in Customise - the old
  // default opened "Reuse a previous design?", a question only repeat
  // customers can answer, as everyone's first screen. Reuse stays fully
  // available as the section above; returning customers with saved spots
  // also jump straight to Customise.
  const [openSection, setOpenSection] = React.useState<string | null>(
    () => "customise",
  );
  // 2026-07-02 CRO 2.5: consume the ?rk_reuse deep link exactly once per
  // page load. The lazy initializer parses AND strips the URL params, so
  // variant-driven remounts (and refreshes) see a clean URL and stay null.
  const [reuseDeepLink] = React.useState<ReuseDeepLink | null>(
    readReuseDeepLink,
  );
  // Same parse-and-strip contract: ?rk_open=1 from the collection hero.
  const [openDeepLink] = React.useState<boolean>(readOpenDeepLink);
  // Abandonment recovery: consume the ?rk_draft resume deep link exactly
  // once per page load. Same lazy parse-and-strip contract as
  // reuseDeepLink above, so refreshes and variant remounts stay null.
  const [draftDeepLink] = React.useState<DraftDeepLink | null>(
    readDraftDeepLink,
  );
  // Section completion ticks. A top-level section earns a pink ✓ in its
  // number badge once the customer has either pressed its Continue/Skip
  // or navigated on to another section while it was continue-able.
  const [completedSections, setCompletedSections] = React.useState<
    Set<string>
  >(() => new Set());
  const markSectionComplete = React.useCallback((id: string) => {
    setCompletedSections((prev) =>
      prev.has(id) ? prev : new Set(prev).add(id),
    );
  }, []);
  // Whether a section is in a state the customer could legitimately
  // Continue from — decides whether navigating away should tick it.
  // Wave 3: only Customise remains a numbered accordion step; the finish
  // screen replaced the addons/notes/approval steps.
  const sectionContinuable = (id: string): boolean => {
    if (id === "customise") return spots.length > 0 && !editorVisible;
    return false;
  };
  const toggleSection = (id: string) => {
    // Opening a different section ticks the one being left, provided it
    // was continue-able (same outcome as pressing its Continue button).
    if (openSection && openSection !== id && sectionContinuable(openSection)) {
      markSectionComplete(openSection);
    }
    setOpenSection((cur) => (cur === id ? null : id));
  };

  const [submitting, setSubmitting] = React.useState(false);
  const [added, setAdded] = React.useState(false);

  // Stuck-helper triggers: exit-intent (desktop pointer leaving via the top)
  // and dwell-without-progress. Runs once per session, never after the
  // design is in the cart. helperProgressRef (declared above) is refreshed
  // each render so the dwell timer reads the latest state without resetting.
  helperProgressRef.current = { spotsLen: spots.length, added };
  React.useEffect(() => {
    if (!STUCK_HELPER_ENABLED) return; // pop-up disabled: no exit-intent / dwell open
    if (typeof window === "undefined") return;
    const fine =
      window.matchMedia && window.matchMedia("(pointer: fine)").matches;
    // Exit intent (desktop only): pointer leaves the top of the viewport.
    const onMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 0 && !e.relatedTarget && !helperProgressRef.current.added) {
        offerHelper();
      }
    };
    if (fine) document.addEventListener("mouseout", onMouseOut);
    // Dwell: interested but not progressing. If after ~50s no spot is saved
    // and nothing is in the cart, offer a hand.
    const dwell = window.setTimeout(() => {
      const p = helperProgressRef.current;
      if (p.spotsLen === 0 && !p.added) offerHelper();
    }, 50000);
    return () => {
      if (fine) document.removeEventListener("mouseout", onMouseOut);
      window.clearTimeout(dwell);
    };
  }, [offerHelper]);

  // Abandoned-design recovery: guest email capture. Logged-in shoppers
  // never see the card (we already have ctx.customerEmail). guestSaved
  // flips the card to its confirmation state once the link has been sent.
  const [guestEmail, setGuestEmail] = React.useState("");
  const [guestConsent, setGuestConsent] = React.useState(false);
  const [guestSaved, setGuestSaved] = React.useState(false);

  // Visual preview drawer. Closed by default — the customer pulls it
  // open with the "Visualise the design" tab.
  const [previewOpen, setPreviewOpen] = React.useState(false);
  // Gate the preview drawer's open/close transition until a frame after
  // it first paints, so it never animates itself into place on load.
  const [pvReady, setPvReady] = React.useState(false);
  // C3: auto-open the preview dock the FIRST time a draft gains content
  // (photo uploaded or text typed) so the visualiser isn't a hidden
  // feature most shoppers never discover. A manual close is sticky —
  // once the customer closes it (or opens then closes it themselves), we
  // never auto-open again.
  const previewAutoOpenedRef = React.useRef(false);
  const previewUserClosedRef = React.useRef(false);

  // Designs from the customer's current cart that can be reused. Re-fetch on
  // mount; the section auto-hides if the list is empty.
  const [reusableDesigns, setReusableDesigns] = React.useState<ReusableDesign[]>([]);

  // Phase B6: the selected garment variant's price (cents), fetched from
  // the storefront product JSON so the foot can show a true item total.
  const [garmentPriceCents, setGarmentPriceCents] = React.useState<
    number | null
  >(null);

  // Ref on the embroidery section so we can smooth-scroll to it after the
  // customer saves or cancels a spot. The editor lives below a tall stack
  // of substeps, so without this they're left stranded at the bottom of
  // the page when the editor collapses.
  const embroideryRef = React.useRef<HTMLElement>(null);
  // Phase B2: ref on the customiser root so the page can glide back up
  // to the top once the flow reaches its final section.
  const rootRef = React.useRef<HTMLDivElement>(null);
  // Ref on the saved-spots tile container so we can scroll to it (where
  // the newly-saved tile + "+ Add another spot" button live). Falls back
  // to the section ref when no spots exist yet (cancel-with-empty-state).
  const savedSpotsRef = React.useRef<HTMLDivElement>(null);
  // Ref on the editor's outer wrap so we can run a measured-pixel height
  // collapse via inline style. The CSS grid-template-rows trick wasn't
  // animating reliably (likely flex-parent quirk), so this is the
  // bulletproof fallback that works in every browser.
  const editorWrapRef = React.useRef<HTMLDivElement>(null);
  // 2026-07-10 (Oscar, mobile): the sticky bottom bar covered the thread
  // picker / Save spot while the customer was ACTIVELY DESIGNING - hide it
  // whenever the open editor is on screen (IntersectionObserver) or the
  // on-screen keyboard is up (an editable inside the customiser has
  // focus), and bring it back when they scroll away from the designer.
  const [editorOnScreen, setEditorOnScreen] = React.useState(false);
  const [typingFocus, setTypingFocus] = React.useState(false);
  const editorVisibleNow = draft !== null;
  React.useEffect(() => {
    if (!editorVisibleNow || typeof IntersectionObserver === "undefined") {
      setEditorOnScreen(false);
      return;
    }
    const el = editorWrapRef.current;
    if (!el) return;
    // 2026-07-24 flicker fix (Oscar). The old observer watched the WHOLE
    // viewport at threshold 0.05 with symmetric toggling, so the bar slid in
    // and out (280ms each way) every time the editor's edge crossed 5%
    // visibility while the customer scrolled a long flow. Two changes:
    //
    // 1. Observe only the bar's own zone. The bar occupies the bottom
    //    ~100-130px; the editor being visible at the TOP of the screen is no
    //    reason to hide it. A negative top rootMargin clips the observed
    //    root to the bottom band, so the bar reacts only when the editor
    //    genuinely reaches the space the bar sits in. (Band height is read
    //    once per editor open; an orientation change mid-edit re-syncs on
    //    the next open, which is acceptable drift.)
    // 2. Hide fast, show slow. Intrusion hides the bar immediately, it is
    //    covering the Save button. Re-showing waits until the zone has
    //    stayed clear for 450ms, so scroll oscillation around the boundary
    //    keeps it hidden rather than flapping it.
    let showTimer = 0;
    const bandPx = 150;
    const topMargin = Math.max(0, (window.innerHeight || 800) - bandPx);
    const io = new IntersectionObserver(
      (entries) => {
        const inBarZone = entries[0]?.isIntersecting ?? false;
        window.clearTimeout(showTimer);
        if (inBarZone) {
          setEditorOnScreen(true);
        } else {
          showTimer = window.setTimeout(() => setEditorOnScreen(false), 450);
        }
      },
      { rootMargin: `-${topMargin}px 0px 0px 0px`, threshold: 0 },
    );
    io.observe(el);
    return () => {
      window.clearTimeout(showTimer);
      io.disconnect();
    };
  }, [editorVisibleNow]);
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const isEditable = (n: EventTarget | null) => {
      const el = n as HTMLElement | null;
      if (!el || !el.tagName) return false;
      return (
        el.tagName === "TEXTAREA" ||
        (el.tagName === "INPUT" &&
          !/^(checkbox|radio|button|submit|range)$/i.test(
            (el as HTMLInputElement).type,
          ))
      );
    };
    const onIn = (e: FocusEvent) => {
      if (isEditable(e.target)) setTypingFocus(true);
    };
    const onOut = () => setTypingFocus(false);
    root.addEventListener("focusin", onIn);
    root.addEventListener("focusout", onOut);
    return () => {
      root.removeEventListener("focusin", onIn);
      root.removeEventListener("focusout", onOut);
    };
  }, []);
  const mobileBarHidden = editorOnScreen || typingFocus;
  // One-fire-per-mount guard for the "Started Custom Design" recovery
  // event (also deduped across remounts by a per-product sessionStorage key).
  const startedFiredRef = React.useRef(false);
  // Abandonment recovery: server draft-sync plumbing. draftSyncRef stays
  // null until an email is known (guest card consent or a logged-in spot
  // save); persistedStateRef always mirrors the newest envelope (written
  // by the persist effect) so the debounced sender reads fresh state
  // without triggering renders.
  const draftSyncRef = React.useRef<{
    email: string;
    source: DraftSyncSource;
    sentFirst: boolean;
  } | null>(null);
  const draftSyncDirtyRef = React.useRef(false);
  const draftSyncTimerRef = React.useRef<number | null>(null);
  const persistedStateRef = React.useRef<PersistedState | null>(null);
  // Wave 1: the editor auto-opens on a fresh load (no persisted work), so
  // rk_design_started must NOT fire on the auto-open - that would count
  // every visitor as a designer. This flag marks "auto-opened, waiting for
  // a real interaction"; the first placement tap / upload / typed text
  // fires the deferred event and clears it. An explicit "Start designing"
  // style tap clears it too (the tap itself is the start signal).
  const pendingAutoStartRef = React.useRef(false);
  // One attempt per mount at the auto-open decision (config load).
  const autoOpenTriedRef = React.useRef(false);
  // Wave 1 funnel: rk_atc_shown fires once, the first render where the
  // add-to-cart actually unlocks. canAddToCart is derived below the early
  // returns, so a render-phase ref mirror feeds the effect (same pattern
  // as helperProgressRef above).
  const atcShownRef = React.useRef(false);
  const canAddToCartLiveRef = React.useRef(false);
  // Wave 3: rk_finish_shown fires once, the first render where the
  // consolidated finish screen appears (>=1 saved spot + not mid-edit).
  const finishShownRef = React.useRef(false);

  // Editor close = matched pair of animations. Both kick off at the same
  // instant so the visual reads as "the editor eases away while the page
  // eases up". Durations are tuned to feel deliberate, not snappy.
  const CLOSE_DURATION_MS = 700;
  const SCROLL_DURATION_MS = 700;

  // 2026-07-24: the App-level scroll is now a thin wrapper over the ONE
  // shared implementation in util/scroll.ts. This file used to carry a
  // near-duplicate that was missing every mobile guard the util had grown
  // (keyboard focus, post-blur reflow window, touch-momentum grace,
  // already-visible skip, shared cancel generation), so every App-level
  // scroll could fire while an input was focused and two loops could fight
  // over scrollY with interleaved absolute writes. That was a large part of
  // Oscar's "scrolls out from underneath you, lands somewhere wrong" iPhone
  // report. One implementation, every guard, everywhere.
  const easedScrollTo = React.useCallback(
    (target: HTMLElement | null, offsetAboveTarget: number) => {
      sharedEasedScrollTo(target, offsetAboveTarget, SCROLL_DURATION_MS);
    },
    [],
  );

  // Animate the editor's height from its current pixel height down to 0,
  // with opacity fading in parallel. Uses the Web Animations API rather
  // than CSS transitions because WAAPI takes explicit start + end values
  // and isn't subject to browser coalescing of rapid style changes.
  //
  // We do NOT honour `prefers-reduced-motion` here. The customer asked
  // for this animation explicitly as a UX affordance, which overrides
  // the OS-level preference for this specific case. The `wrap.animate`
  // check still bails on browsers without WAAPI support.
  const easedCollapse = React.useCallback((onDone: () => void) => {
    const wrap = editorWrapRef.current;
    if (!wrap || typeof wrap.animate !== "function") {
      onDone();
      return;
    }
    const h = wrap.getBoundingClientRect().height;
    wrap.style.overflow = "hidden";
    const animation = wrap.animate(
      [
        { height: `${h}px`, opacity: 1 },
        { height: "0px", opacity: 0 },
      ],
      {
        duration: CLOSE_DURATION_MS,
        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
        fill: "forwards",
      },
    );
    let unmounted = false;
    const safeDone = () => {
      if (unmounted) return;
      unmounted = true;
      onDone();
    };
    animation.onfinish = safeDone;
    // Belt-and-braces: some browsers silently skip onfinish under
    // background-tab throttling.
    window.setTimeout(safeDone, CLOSE_DURATION_MS + 80);
  }, []);

  // True once a config has successfully loaded. A LATER failure must not be
  // treated the same as never having one: see the catch below.
  const configEverLoadedRef = React.useRef(false);
  React.useEffect(() => {
    let cancelled = false;
    fetchConfig(ctx.workerUrl, ctx.productGid)
      .then((c) => {
        if (cancelled) return;
        configEverLoadedRef.current = true;
        setConfig(c);
        injectFontFaces(c.fonts);
        // Phase 35h: emit a template_view once the customiser has
        // resolved its config. Carries the template name + tag so
        // analytics can attribute customisation drop-off per template.
        emitAnalytics("template_view", {
          productGid: ctx.productGid,
          productHandle: ctx.productHandle,
          variantId: ctx.variantId,
        });
      })
      .catch((e: Error) => {
        if (cancelled) return;
        // Only fatal when there is nothing to show. Once a config has loaded,
        // a later failure must never reach `err`: that branch (see "FAIL
        // OPEN" below) replaces the ENTIRE customiser with a banner AND
        // un-tags every /cart/add form, restoring the theme's own add-to-cart.
        // Mid-design that would bin the customer's work and let them buy the
        // $239 gift box with no design attached, triggered by nothing worse
        // than a dropped request.
        if (!configEverLoadedRef.current) setErr(e.message);
      });
    return () => {
      cancelled = true;
    };
    // ctx.variantId is deliberately NOT a dependency. fetchConfig (above)
    // takes only workerUrl + productGid, so keying on the variant refetched
    // the whole config on every size or colour change for no benefit. It also
    // re-fired template_view each time, which is not a private counter: it
    // forwards to GA4 as rk_customiser_view and to the Meta pixel, so a
    // customer trying six colours inflated the customiser-view denominator
    // sixfold and polluted a Meta audience signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.workerUrl, ctx.productGid, ctx.productHandle]);

  // Once config has loaded (so the preview drawer has rendered), wait one
  // frame, then enable its transitions. The drawer's first paint is in
  // its closed state with no transition, so it can't slide on load.
  React.useEffect(() => {
    if (!config) return;
    const id = requestAnimationFrame(() => setPvReady(true));
    return () => cancelAnimationFrame(id);
  }, [config]);

  // Once a stage owns a real option axis (Stage.variantAxis), the theme must
  // stop offering its own control for it, or the customer sees two pickers for
  // one choice. At the same time, reveal the labels on the rows they DO still
  // use: the theme renders every option name as a <legend class="sr-only">
  // (templates/product.json sets hide_option_labels), so on a 3-axis bundle the
  // customer just sees three unlabelled rows of pills.
  //
  // Done from JS against the specific elements rather than as a CSS rule, for
  // three reasons. It cannot leak to another product, because it only runs
  // where a stage actually declares the axis. It reverses itself on cleanup,
  // so if the customiser ever stops rendering the picker the native control
  // comes straight back. And it never runs at all when config failed to load,
  // which is the fail-open path where the theme has to own the sale.
  //
  // CRITICAL: hide with display:none and never remove or uncheck the input.
  // assets/product.js getOptions() collects [data-single-option-selector] in
  // DOM order and pushes only CHECKED radios, so a detached or unchecked
  // colour row makes the array two long, getVariantFromOptionArray misses,
  // current_variant goes undefined and price plus add-to-cart die.
  React.useEffect(() => {
    if (!config) return;
    const owned = (stagesFor(config.templateTag) ?? [])
      .map((s) => s.variantAxis)
      .filter((a): a is string => !!a)
      .map((a) => a.trim().toLowerCase());
    if (owned.length === 0) return;
    const hiddenRows: HTMLElement[] = [];
    const revealedLabels: HTMLElement[] = [];
    try {
      const radios = Array.from(
        document.querySelectorAll<HTMLInputElement>(
          'input[type="radio"][data-single-option-selector]',
        ),
      );
      for (const r of radios) {
        const m = /options\[([^\]]+)\]/.exec(r.name || "");
        if (!m) continue;
        const row = r.closest<HTMLElement>("fieldset");
        if (!row) continue;
        if (owned.includes(m[1].trim().toLowerCase())) {
          if (row.style.display !== "none") {
            row.style.display = "none";
            hiddenRows.push(row);
          }
        } else {
          const legend = row.querySelector<HTMLElement>("legend.sr-only");
          if (legend) {
            legend.classList.remove("sr-only");
            revealedLabels.push(legend);
          }
        }
      }
    } catch {
      /* cosmetics must never take the customiser down */
    }
    return () => {
      hiddenRows.forEach((el) => {
        el.style.display = "";
      });
      revealedLabels.forEach((el) => {
        el.classList.add("sr-only");
      });
    };
  }, [config]);

  // C3: auto-open the preview dock once the draft first has something to
  // show. Only fires when the template actually renders the visualiser,
  // only once, and never against a manual close.
  React.useEffect(() => {
    if (!config?.product.visualPreview) return;
    if (previewAutoOpenedRef.current || previewUserClosedRef.current) return;
    const hasContent =
      !!draft && (!!draft.uploadStagingKey || draft.text.trim().length > 0);
    if (hasContent) {
      previewAutoOpenedRef.current = true;
      setPreviewOpen(true);
    }
  }, [draft, config]);

  // Phase 67h+i: prune stale persisted state. Runs once whenever config
  // loads. Drops saved spots whose placementSlug no longer exists in the
  // live config (rk-pet-varsity strip-and-re-attach scripts can leave
  // sessionStorage spots that point at deleted placements). If pruning
  // empties the spots list AND there's no open draft, kick a fresh draft
  // so the editor body still renders. Same fix for the draft itself if
  // it references a missing slug — we drop the slug only, preserving any
  // typed text / uploaded photo / colour picks.
  React.useEffect(() => {
    if (!config) return;
    const liveSlugs = new Set((config.placements ?? []).map((p) => p.slug));
    const prunedSpots = spots.filter(
      (s) => !!s.placementSlug && liveSlugs.has(s.placementSlug),
    );
    const spotsWereEmptied = spots.length > 0 && prunedSpots.length === 0;
    if (prunedSpots.length !== spots.length) {
      setSpots(prunedSpots);
    }
    if (!draft && spotsWereEmptied) {
      setDraft(emptyDraft());
    } else if (
      draft &&
      draft.placementSlug &&
      !liveSlugs.has(draft.placementSlug)
    ) {
      setDraft({ ...draft, placementSlug: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // Once the config arrives, pre-lock the flow-specific upsell on
  // pet/kid/person templates. The placement itself is intentionally NOT
  // pre-selected: the customer consciously picks their first embroidery
  // location (2026-07-02 - Oscar: auto-picking the template default, e.g.
  // centre-front on the crewneck, put people on a placement they didn't
  // choose). The one-tap select-and-advance still keeps the placement
  // step to a single interaction.
  //
  // Phase 36: pre-lock the flow-specific upsell on pet/kid
  // templates. The mode picker is hidden on those flows so the draft's
  // mode must already be set to "logo" + addonUpsellId must point at
  // the pet/kid upsell, otherwise the upload step won't show.
  React.useEffect(() => {
    if (!config) return;
    // Phase 70: resolve the design upsell via the FlowBlueprint instead
    // of the legacy magic-kind lookup. The helper falls back to the
    // old lookup when the blueprint isn't bound yet (migration window).
    const flowUpsell = designUpsellFor(config);
    setDraft((d) => {
      if (!d) return d;
      if (spots.length > 0 && d.placementSlug) return d;
      // Build patches without overwriting customer edits.
      const next = { ...d };
      if (flowUpsell) {
        next.mode = "logo";
        next.addonUpsellId = flowUpsell.id;
        if (next.addonCount == null) {
          next.addonCount = flowUpsell.countMax && flowUpsell.countMax > 1 ? 1 : null;
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // Wave 1 (kill the vestigial "Start designing" tap): once config
  // resolves on a fresh visit - no saved spots, no restored draft, no
  // reorder deep link - open the editor immediately so the placement
  // picker is the first thing the customer sees. rk_design_started is
  // deferred to the first real interaction (pendingAutoStartRef) so the
  // auto-open doesn't count every page view as a started design. The
  // "Start designing" button still renders for the states this doesn't
  // cover (customer cancels the draft / deletes every spot mid-session).
  React.useEffect(() => {
    if (!config || autoOpenTriedRef.current) return;
    autoOpenTriedRef.current = true;
    if (ctx.missingOption) return; // the pick-your-size gate renders instead
    if (reuseDeepLink) return; // the reorder deep link owns the opening state
    if (draftDeepLink) return; // a resume deep link is hydrating the saved design
    if (spots.length > 0) return; // restored work wins
    // Staged templates open from a stage CTA and nothing else. Auto-opening
    // here put an UNSCOPED editor under the stage list on first load, showing
    // every garment's placements at once (centre, left chest, sleeve, hat back,
    // sock) plus "No placement chosen". That is the confusing first screen:
    // the customer is asked to pick a placement before they have picked a
    // garment. A pristine restored draft is cleared for the same reason.
    if (stagesFor(config.templateTag)) {
      if (
        draft !== null &&
        !draft.placementSlug &&
        !draft.uploadStagingKey &&
        !draft.text.trim()
      ) {
        setDraft(null);
      }
      return;
    }
    if (draft !== null) {
      // A restored-but-untouched draft (e.g. a previous visit's auto-open
      // persisting, or a variant-driven remount) means the editor is
      // already open - just re-arm the deferred design_started so the
      // funnel still counts their first real interaction.
      const pristine =
        !draft.placementSlug && !draft.uploadStagingKey && !draft.text.trim();
      if (pristine) pendingAutoStartRef.current = true;
      return;
    }
    pendingAutoStartRef.current = true;
    openNewSpot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // Wave 1: deferred rk_design_started for the auto-opened editor. An
  // upload or typed text is unmistakably a real design action (covers
  // single-placement templates where the placement auto-picks and the
  // customer never taps a placement card). Placement-card taps route
  // through handlePlacementPicked below.
  React.useEffect(() => {
    if (!pendingAutoStartRef.current || !draft) return;
    if (draft.uploadStagingKey || draft.text.trim()) {
      pendingAutoStartRef.current = false;
      emitDesignStartedEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Wave 1 funnel: rk_atc_shown once per mount, on the first render where
  // the pink CTA actually unlocks. Dep-less on purpose: canAddToCart is
  // derived below the early returns and mirrored into canAddToCartLiveRef
  // there, so this cheap guard runs after every render.
  React.useEffect(() => {
    if (atcShownRef.current || !canAddToCartLiveRef.current) return;
    atcShownRef.current = true;
    emitAnalytics("atc_shown", {
      productGid: ctx.productGid,
      productHandle: ctx.productHandle,
      variantId: ctx.variantId,
      spotCount: spots.length,
      flowKind: flowKindFor(config),
    });
  });

  // Wave 3 funnel: rk_finish_shown once, the first render where the
  // consolidated finish screen shows (>=1 saved spot + no open editor).
  // Dep-less + ref-guarded, same shape as rk_atc_shown above. On that
  // first appearance we also emit one rk_finish_addon_view impression per
  // eligible paid add-on card (gift box / rush) so the shelf's reach is
  // measurable. `draft === null` mirrors the approvalSectionVisible gate
  // (editorVisible) without touching the const declared below the early
  // returns.
  React.useEffect(() => {
    if (finishShownRef.current || !config || spots.length === 0 || draft !== null)
      return;
    finishShownRef.current = true;
    const base = {
      productGid: ctx.productGid,
      productHandle: ctx.productHandle,
      variantId: ctx.variantId,
    };
    emitAnalytics("finish_shown", {
      ...base,
      spotCount: spots.length,
      flowKind: flowKindFor(config),
    });
    for (const u of eligibleOrderUpsells(config, spots)) {
      emitAnalytics("finish_addon_view", {
        ...base,
        addonKind: u.kind,
        upsellId: u.id,
        upsellLabel: u.label,
        upsellPriceCents: u.priceCents,
      });
    }
  });

  // Build the reusable-design list: designs decoded from /cart.js PLUS
  // the localStorage recent-designs list (a reliable fallback for when a
  // cart app has rewritten the line-item properties). De-duplicated.
  // Runs on mount + after every add-to-cart.
  const refreshCart = React.useCallback(async () => {
    const cart = await fetchCart();
    const fromCart = decodeReusableDesigns(cart);
    const seen = new Set(fromCart.map((d) => designSignature(d.draft)));
    const merged: ReusableDesign[] = [...fromCart];
    for (const r of loadRecentDesigns()) {
      const sig = designSignature(r.draft);
      if (seen.has(sig)) continue;
      seen.add(sig);
      merged.push({
        key: `recent-${r.ts}-${merged.length}`,
        sourceLabel: r.sourceLabel,
        draft: r.draft,
      });
    }
    setReusableDesigns(merged);
    // Phase 88: count qualifying customiser units currently in the cart so
    // the discount tier carries across multiple ATCs. Reads the same
    // /cart.js the reuse decoder reads - free piggyback. A customiser line
    // is identified by the `_rk_group_id` property the builder writes
    // (handle-independent, covers every customiser product); the surcharge
    // fee line (`_added_by: rk-builder-surcharge`) is excluded because the
    // fee product isn't in the discount collection, matching what the
    // Shopify Function counts at checkout.
    if (cart && Array.isArray(cart.items)) {
      let count = 0;
      const unitPrices: number[] = [];
      for (const item of cart.items) {
        const props = item.properties ?? null;
        if (!props || !props["_rk_group_id"]) continue;
        if (props["_added_by"] === "rk-builder-surcharge") continue;
        const qty = item.quantity ?? 1;
        count += qty;
        // Per-unit garment price (raw /cart.js lines are pre-transform,
        // so garment lines are garment-only priced). Drives the preview's
        // global price ranking so it matches the checkout Function.
        const unit = typeof item.price === "number" ? item.price : 0;
        for (let q = 0; q < qty; q++) unitPrices.push(unit);
      }
      setExistingCustomCountInCart(count);
      setExistingCustomUnitPricesCents(unitPrices);
    } else {
      setExistingCustomCountInCart(0);
      setExistingCustomUnitPricesCents([]);
    }
    // Threshold progress ("you're $X off free socks") must not be inflated by
    // service fees: the $150 Father's Day express is a delivery upgrade, not
    // garment spend (Oscar 2026-08-24). Same exclusion as the cart drawer's
    // qualifyingSubtotal, the checkout Validation Function and the
    // orders/create gift guard - all four have to agree.
    let subtotalCents =
      cart && typeof cart.items_subtotal_price === "number"
        ? cart.items_subtotal_price
        : 0;
    if (cart && Array.isArray(cart.items)) {
      for (const item of cart.items) {
        const props = item.properties ?? null;
        if (props && props["_rk_cart_addon"] === "fathers_day_rush") {
          const linePrice =
            typeof item.line_price === "number" ? item.line_price : 0;
          subtotalCents -= linePrice;
        }
      }
    }
    setCartSubtotalCents(Math.max(0, subtotalCents));
  }, []);
  React.useEffect(() => {
    void refreshCart();
  }, [refreshCart]);

  // 2026-07-02 CRO 2.5: a reorder deep link opens the flow on the Reuse
  // section (ReuseSection itself pre-fills the order form + fires the
  // lookup via its deepLink prop). A restored sessionStorage draft would
  // keep the section collapsed (open requires !editorVisible), so an
  // UNTOUCHED restored draft (no placement, upload, or text) is dropped;
  // one with real work in it wins over the deep link.
  React.useEffect(() => {
    if (!reuseDeepLink) return;
    setOpenSection("reuse");
    setDraft((d) => {
      if (!d) return d;
      const untouched =
        !d.placementSlug && !d.uploadStagingKey && !d.text.trim();
      return untouched ? null : d;
    });
    emitAnalytics("reuse_deeplink", {
      productGid: ctx.productGid,
      productHandle: ctx.productHandle,
      variantId: ctx.variantId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reuseDeepLink]);

  // ?rk_open=1: ease the customer down into the builder once it actually has
  // height. Gated on `config` because before it loads the customiser is a
  // skeleton and we would scroll to the wrong Y, and on a ref so a re-render
  // never yanks the page after they have started scrolling themselves.
  // A single rAF is NOT enough here, and shipping that was wrong: the boot
  // script MOVES the host out of its initial off-screen slot and into the
  // product form (it lands inside .product-block-buy-buttons), then reveals
  // it. Scrolling before that finishes aims at a Y the element promptly
  // leaves, which is exactly what happened live: the param stripped, no
  // scroll. So poll until the element is genuinely visible AND has held the
  // same position for two consecutive samples, then scroll once.
  const openDeepLinkFired = React.useRef(false);
  React.useEffect(() => {
    if (!openDeepLink || !config || openDeepLinkFired.current) return;
    // Where the page sat when we started. Compare against THIS, never against
    // zero: browsers restore scroll position on a repeat visit to the same
    // URL, and an absolute `scrollY > 0` test reads that restored position as
    // "the customer is already scrolling" and silently kills the deep link.
    // That bug cost a deploy: the param stripped and nothing moved.
    const startY = window.scrollY;
    let cancelled = false;
    let timer = 0;
    let lastTop: number | null = null;
    let tries = 0;
    // ANY user input kills the pending arrival scroll, not just >40px of
    // downward drift. The old guard let the poll yank the page up to 15s
    // after load for someone who had flicked upward or tapped, which on a
    // phone reads as the page moving on its own.
    const onUserInput = () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    const inputOpts: AddEventListenerOptions = { passive: true, capture: true };
    window.addEventListener("touchstart", onUserInput, inputOpts);
    window.addEventListener("wheel", onUserInput, inputOpts);
    window.addEventListener("keydown", onUserInput, inputOpts);
    const go = (el: HTMLElement) => {
      openDeepLinkFired.current = true;
      easedScrollTo(el, Math.round((window.innerHeight || 800) * 0.12));
      emitAnalytics("open_deeplink", {
        productGid: ctx.productGid,
        productHandle: ctx.productHandle,
        variantId: ctx.variantId,
      });
    };
    const tick = () => {
      if (cancelled || openDeepLinkFired.current) return;
      // A genuine user scroll always wins, but only a genuine one.
      if (window.scrollY > startY + 40) return;
      tries += 1;
      const el = rootRef.current;
      const rect = el?.getBoundingClientRect();
      const visible =
        !!el &&
        !!rect &&
        rect.height > 0 &&
        // Still parked off-screen at left:-9999px, or not revealed yet.
        rect.left > -1000 &&
        window.getComputedStyle(el).visibility !== "hidden";
      if (visible) {
        const top = Math.round(rect!.top + window.scrollY);
        // Two stable samples = the boot script has finished relocating it.
        if (lastTop !== null && Math.abs(top - lastTop) < 2) return go(el!);
        lastTop = top;
        // Budget spent but it IS on the page: scroll anyway. A late scroll
        // beats silently doing nothing, which is what the customer would
        // otherwise get after clicking a button that promised the builder.
        if (tries >= 110) return go(el!);
      }
      // ~15s of patience. The first budget here was 3s and that was too
      // short: this PDP is heavy (6000px, 385KB bundle) and the boot script
      // did not reveal the host until somewhere between 3 and 4 seconds, so
      // the poll gave up while the host was still parked off-screen and the
      // deep link did nothing at all. Waiting costs nothing, since a user
      // scroll cancels it and the fired-ref makes it single-shot.
      if (tries < 120) timer = window.setTimeout(tick, 120);
    };
    timer = window.setTimeout(tick, 150);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener("touchstart", onUserInput, inputOpts);
      window.removeEventListener("wheel", onUserInput, inputOpts);
      window.removeEventListener("keydown", onUserInput, inputOpts);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDeepLink, config]);

  // Abandonment recovery: a ?rk_draft resume deep link fetches the
  // server-saved BuilderDraft and rebuilds the customiser exactly where
  // the customer left off - on any device, since the state lives behind
  // the (draftId, email) pair rather than this browser's localStorage.
  // The guard refuses a state that belongs to a different product (link
  // followed on the wrong PDP); on that, or any lookup miss, the
  // customer silently gets the normal fresh flow. Runs once per page
  // load (the lazy deep-link state is consumed + stripped on first
  // render, so remounts see null).
  React.useEffect(() => {
    const link = draftDeepLink;
    if (!link || !link.email) return;
    const email = link.email;
    let cancelled = false;
    void (async () => {
      const res = await lookupDraft(ctx.workerUrl, link.draftId, email);
      if (cancelled || !res.ok) return;
      const state = guardRestoredState(
        res.state as (PersistedState & { productGid?: string }) | null,
        ctx.productGid,
      );
      if (!state) return;
      // Persist FIRST so a variant-driven remount re-hydrates the same
      // design, then drive the live setters.
      savePersisted(ctx.productGid, state);
      applyRestoredState(state);
      setGuestEmail(email);
      // Keep the server copy fresh as the customer edits on this device.
      armDraftSync(email, "guest_card", false);
      emitAnalytics("draft_resumed", {
        productGid: ctx.productGid,
        productHandle: ctx.productHandle,
        variantId: ctx.variantId,
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftDeepLink]);

  // Phase B6: fetch the selected garment variant's price so the foot can
  // show "Item total" = garment + embroidery. Re-runs on variant change.
  React.useEffect(() => {
    let cancelled = false;
    if (!ctx.variantId || !ctx.productHandle) {
      setGarmentPriceCents(null);
      return;
    }
    fetchVariantPriceCents(ctx.productHandle, ctx.variantId).then((c) => {
      if (!cancelled) setGarmentPriceCents(c);
    });
    return () => {
      cancelled = true;
    };
  }, [ctx.productHandle, ctx.variantId]);

  // Reset `added` whenever the customer makes more edits after a successful
  // add-to-cart so the banner doesn't linger forever. MUST sit above any
  // early returns. Hooks have to be called in the same order every render.
  React.useEffect(() => {
    if (added) setAdded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spots, draft]);

  // Reset the approval pick when the customer changes their design (added,
  // removed, or edited a spot). Force a fresh decision so an old "approve"
  // pick can't ride along after the design has changed significantly.
  //
  // Audit 2026-10: keyed on a CONTENT signature, not the array identity,
  // and skipped on the first run. The effect used to fire on mount too, so
  // the persisted "Approve before stitching (+$15)" pick was thrown away on
  // every reload (and on any host re-attach that mounts a fresh App). Now a
  // mount with identical spots keeps the choice; only a real change to the
  // spots resets it.
  const spotsSigRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const sig = JSON.stringify(spots);
    const prev = spotsSigRef.current;
    spotsSigRef.current = sig;
    if (prev === null || prev === sig) return;
    setApprovalChoice("skip");
    setApprovalAddonId(null);
  }, [spots]);

  // Phase B2: when the Customise section opens, ease the page to it so
  // the customer follows the flow rather than being left behind.
  const prevCustomiseOpenRef = React.useRef(
    draft !== null || openSection === "customise",
  );
  React.useEffect(() => {
    const open = draft !== null || openSection === "customise";
    if (open && !prevCustomiseOpenRef.current) {
      easedScrollTo(
        embroideryRef.current,
        Math.round((window.innerHeight || 800) * 0.4),
      );
    }
    prevCustomiseOpenRef.current = open;
  }, [draft, openSection]);

  // Phase 35e: persist + variant tracking. Save on every state change so
  // a variant switch (embed.ts re-renders the same App instance with a new
  // ctx) and a reload pick up where the customer left off.
  //
  // Audit 2026-10 (reviewed): once the design is in the cart (`added`) this
  // effect must NOT write any more. onAddToCart used to call
  // clearPersisted() inline and then setDesignGroupId(null); that state
  // change re-ran this effect in the same commit and wrote the cleared
  // design straight back, so it reappeared on reload. A sibling effect
  // that cleared after this one was tried and rejected: any later re-run
  // (variant switch, notes, add-ons, bundle picks, approval) wrote the
  // carted envelope back again while `added` stayed true. Gating HERE is
  // state-keyed: every re-run while carted clears instead of writing, and
  // writing resumes the moment `added` flips false on the next spots/draft
  // edit (the reset effect above). Persisted shape is untouched.
  React.useEffect(() => {
    if (added) {
      clearPersisted(ctx.productGid);
      return;
    }
    const state: PersistedState = {
      spots,
      approvalChoice,
      approvalAddonId,
      customerNotes,
      orderAddOnIds,
      fieldValues,
      selectedBundleIds: Array.from(selectedBundleIds),
      bundleMemberVariantPicks,
      lastVariantId: ctx.variantId,
      draft,
      groupId: designGroupId,
    };
    savePersisted(ctx.productGid, state);
    // Abandonment recovery: mirror the freshest envelope for the server
    // draft sync and, once an email is known (draftSyncRef armed),
    // piggyback the debounced re-save here so the BuilderDraft row
    // tracks what's on screen. First save fires immediately; bursts of
    // edits then collapse into one POST per DRAFT_SYNC_DEBOUNCE_MS.
    persistedStateRef.current = state;
    if (draftSyncRef.current) {
      draftSyncDirtyRef.current = true;
      scheduleDraftSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [added, spots, approvalChoice, approvalAddonId, customerNotes, orderAddOnIds, fieldValues, selectedBundleIds, bundleMemberVariantPicks, ctx.productGid, ctx.variantId, draft, designGroupId]);

  // Abandonment recovery: clear any pending debounced draft-save timer on
  // unmount (variant switches remount the whole customiser; the fresh
  // mount re-arms the sync at the next email-bearing moment).
  React.useEffect(() => {
    return () => {
      if (draftSyncTimerRef.current != null) {
        window.clearTimeout(draftSyncTimerRef.current);
        draftSyncTimerRef.current = null;
      }
    };
  }, []);

  // Phase 81: effective per-member price - honours the customer's
  // inline variant pick when set, else falls back to the cached
  // first-variant price. Used by both bundleAddCents (foot total) and
  // the pet-discount preview so the savings UI never lags the picker.
  const effectiveBundleMemberPriceCents = React.useCallback(
    (memberId: string): number | null => {
      const pick = bundleMemberVariantPicks[memberId];
      if (pick) {
        const data = bundleMemberProductData[memberId];
        const v = data?.variants?.find((x) => String(x.id) === pick);
        if (v && typeof v.price === "number") return v.price;
      }
      const fallback = bundleMemberPrices[memberId];
      return typeof fallback === "number" ? fallback : null;
    },
    [bundleMemberVariantPicks, bundleMemberProductData, bundleMemberPrices],
  );

  // Phase 88: unified custom multi-buy discount preview (pet + kid +
  // make-your-own are one discount now). Mirrors the live Shopify Function
  // tier (rk-pet-tiered-discount, gated on the "RK Custom Multi-Buy" smart
  // collection): 1st full, 2nd 15% off, 3rd+ 20% off.
  //
  // CRITICAL: this useMemo MUST sit above every early return so the hook
  // count stays stable across renders (rules of hooks). A previous attempt
  // placed it below `if (!config) return ...` and silently broke the entire
  // customiser on every PDP. Don't move it back down.
  //
  // The primary product always qualifies (the customiser only loads on
  // products in the collection by construction); a bundle member qualifies
  // when it matched a live customiser template (non-null `flowKind`). The
  // memo returns a zero result until at least 2 qualifying units exist, so
  // it's a no-op for a lone item.
  const customDiscounts = React.useMemo(() => {
    // Multi-buy disabled: no preview savings anywhere (strike-throughs,
    // "you saved", the foot's Item-total discount all read from this).
    if (!MULTIBUY_ENABLED) {
      return { primaryDiscountPct: 0, memberDiscountPct: {}, totalSavingsCents: 0 };
    }
    if (!config) {
      return {
        primaryDiscountPct: 0,
        memberDiscountPct: {} as Record<string, number>,
        totalSavingsCents: 0,
      };
    }
    const members = (config.bundleMembers ?? []).map((m) => ({
      id: m.id,
      flowKind: m.flowKind ?? null,
      included: !m.optional || selectedBundleIds.has(m.id),
      // Phase 81: prefer the customer's picked variant price so the
      // savings strip matches the live foot total when a variant
      // costs more / less than the default.
      priceCents: effectiveBundleMemberPriceCents(m.id),
      quantity: m.quantity ?? 1,
    }));
    return assignCustomDiscounts({
      primaryQualifies: true,
      primaryPriceCents: garmentPriceCents,
      members,
      existingUnitPriceCents: existingCustomUnitPricesCents,
    });
  }, [
    config,
    garmentPriceCents,
    effectiveBundleMemberPriceCents,
    selectedBundleIds,
    existingCustomUnitPricesCents,
  ]);

  // 2026-07-03: is the currently-selected size a sold-out pre-order? Drives
  // the ATC label + a note. MUST be declared here, above every early return,
  // so the hook order is stable (a useMemo after an early return crashes with
  // React #310). Mirrors the add-to-cart branch's gate.
  const currentPreorder = React.useMemo(() => {
    const off = {
      active: false,
      dollars: null as number | null,
      sizeLabel: "",
      etaText: null as string | null,
    };
    if (!PREORDER_ENABLED || !config) return off;
    const v = (productJson?.variants ?? []).find(
      (x) => String(x.id) === String(ctx.variantId),
    );
    if (v?.available !== false) return off;
    const map = config.settings?.preorderVariantMap ?? {};
    // MUST mirror the add-to-cart branch: full price since 2026-07-25.
    const dollars =
      garmentPriceCents != null
        ? Math.floor(garmentPriceCents / 100)
        : null;
    const active = dollars != null && dollars > 0 && map[String(dollars)] != null;
    // Real supplier next-delivery date for THIS sold-out variant, if we have a
    // future one; otherwise null => generic copy.
    const etaText = formatPreorderEta(
      config.settings?.variantEtaMap?.[String(ctx.variantId)],
    );
    return {
      active,
      dollars,
      sizeLabel: v?.title || ctx.garmentColourName || "this size",
      etaText,
    };
  }, [config, productJson, ctx.variantId, ctx.garmentColourName, garmentPriceCents]);

  // Variant gate: if the launcher couldn't resolve a variantId yet, nudge
  // the customer back to the variant picker before letting them design.
  if (ctx.missingOption) {
    return (
      <div className="rk-pc-root">
        <div className="rk-pc-gate">
          <div className="rk-pc-gate__title">Pick your {ctx.missingOption} first</div>
          <div className="rk-pc-gate__sub">
            Choose a {ctx.missingOption} above to start customising.
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    // 2026-07-02: FAIL OPEN. The embed eagerly hides every native
    // add-to-cart button before the config loads; if the config then fails
    // this banner used to be a hard dead end with NO way to buy (this
    // exact failure happened in the 2026-06-30 Neon outage). Un-tag the
    // forms so the theme's own ATC (and, via the :has() rule, the qty
    // selector) come back, and swap the raw error for honest copy.
    try {
      document
        .querySelectorAll("form[action*='/cart/add'][data-rk-pc-active]")
        .forEach((f) => f.removeAttribute("data-rk-pc-active"));
    } catch {
      /* never let the restore itself throw */
    }
    return (
      <div className="rk-pc-root">
        <div className="rk-pc-banner is-error">
          The customiser is briefly unavailable. You can order the garment
          now and email your design to support@rkthreads.com, or refresh to
          try again.
        </div>
      </div>
    );
  }

  if (!config) {
    // Phase 35f: branded skeleton instead of a plaintext "Loading…".
    // Mirrors the section structure the customer is about to see so
    // the layout doesn't jump on hydration.
    return (
      <div className="rk-pc-root">
        <div className="rk-pc-skeleton rk-pc-skeleton--sec">
          <div className="rk-pc-skeleton__head">
            <div className="rk-pc-skeleton__num" />
            <div className="rk-pc-skeleton__title" />
          </div>
          <div className="rk-pc-skeleton__body">
            <div className="rk-pc-skeleton__tiles">
              <div className="rk-pc-skeleton__tile" />
              <div className="rk-pc-skeleton__tile" />
              <div className="rk-pc-skeleton__tile" />
            </div>
          </div>
        </div>
        <div className="rk-pc-skeleton rk-pc-skeleton--sec">
          <div className="rk-pc-skeleton__head">
            <div className="rk-pc-skeleton__num" />
            <div className="rk-pc-skeleton__title" />
          </div>
        </div>
      </div>
    );
  }

  // Phase 35f: empty-state guard. A template that's live but missing
  // placements means the admin saved a half-configured template. Show
  // a friendly customer-facing fallback instead of a broken-looking
  // editor with no tiles.
  if (!config.placements || config.placements.length === 0) {
    return (
      <div className="rk-pc-root">
        <div className="rk-pc-gate">
          <div className="rk-pc-gate__title">
            Customisation coming soon on this product
          </div>
          <div className="rk-pc-gate__sub">
            Our embroidery options for this style are being set up. Check
            back shortly, or grab the base version now and we'll add
            embroidery details on request via email.
          </div>
        </div>
      </div>
    );
  }

  // ── Derived data per render ────────────────────────────────────────────
  const lockedSlugs = lockedSlugsFor(spots, config.placements, editingSpotId);
  const priorLogos = priorLogosFrom(spots);
  const totalCents = totalCentsFor(spots, config, approvalChoice, approvalAddonId, orderAddOnIds, fieldValues);
  // C2: PROVISIONAL total for the display surfaces (section-header price,
  // foot "Item total", mobile sticky bar). totalCentsFor() sums SAVED spots
  // only, so a paid choice inside the open draft (oversize tier, pet/kid
  // scan, coloured + filled, sleeve emoji) doesn't hit the headline total
  // until the customer presses "Save spot" — a surprise cost at the moment
  // of commitment. Folding the in-progress draft in as a would-be spot lets
  // those choices reflect live. DISPLAY ONLY: the cart payload (onAddToCart
  // / propertiesFor) still uses `totalCents` over saved spots, because an
  // unsaved draft isn't added. A draft with no priceable content adds 0, so
  // this equals `totalCents` until a real fee is chosen.
  const draftSpotForTotal: Spot | null = draft
    ? { ...draft, id: editingSpotId ?? "__draft_preview__" }
    : null;
  // C2 fix: when editing an existing saved spot, that spot is still present in
  // `spots` (saveSpot replaces it in place), so folding the draft in over the
  // full `spots` array double-counts its fees AND adds a phantom
  // additional-placement fee from totalCentsFor's index-based ladder — the
  // displayed total inflates until Save. Base the projection on a list that
  // EXCLUDES the spot being edited.
  const baseSpotsForTotal = editingSpotId
    ? spots.filter((s) => s.id !== editingSpotId)
    : spots;
  const displayTotalCents = draftSpotForTotal
    ? totalCentsFor(
        [...baseSpotsForTotal, draftSpotForTotal],
        config,
        approvalChoice,
        approvalAddonId,
        orderAddOnIds,
        fieldValues,
      )
    : totalCents;
  const editorVisible = draft !== null;
  // Phase B2: the Customise section is force-open while the editor is up.
  const customiseOpen = editorVisible || openSection === "customise";
  // Approval section appears once the customer has at least one saved spot
  // and is NOT mid-edit. That's the natural "you've finished a design,
  // tell us how to proceed" moment in the flow.
  const approvalSectionVisible = spots.length > 0 && !editorVisible;
  // Wave 3: the eligible paid order add-ons for the finish screen. Shared
  // by the FinishSection render and the finish_addon_view impressions, so
  // the tiles shown and the impressions logged never diverge.
  const finishOrderAddons = eligibleOrderUpsells(config, spots);
  // Phase 81: a ticked bundle member with >1 variants AND no customer
  // pick blocks ATC - we don't want to silently auto-pick "Black, S"
  // for them. Compute the first unconfigured member so the UI can
  // surface a specific "Pick options for {name}" message.
  const firstUnpickedBundleMember = (config.bundleMembers ?? []).find((m) => {
    if (!selectedBundleIds.has(m.id)) return false;
    const data = bundleMemberProductData[m.id];
    const variants = data?.variants ?? [];
    if (variants.length <= 1) return false; // single-variant rows are auto-picked
    return !bundleMemberVariantPicks[m.id];
  }) ?? null;
  // Phase 81: split the gate. The bundle preview section + savings
  // strips appear once the customer has finished designing (spots +
  // variant + approval) - WITHOUT requiring every bundle pick to be
  // made, otherwise the picker UI hides itself when it's needed most.
  // The ATC button stays disabled while a bundle pick is missing.
  // Garment stages. Null on every single-garment template, which leaves the
  // original single-list behaviour untouched.
  const stages = stagesFor(config.templateTag);
  const stageExtras = stages
    ? unstagedSlugs(stages, config.placements.map((p) => p.slug))
    : [];
  const spotsForStage = (st: Stage) =>
    spots.filter((s) => !!s.placementSlug && st.slugs.includes(s.placementSlug));
  /** A saved spot no stage claims (including one with no placement yet). */
  const isUnstagedSpot = (s: Spot) =>
    !stages || !s.placementSlug || !stageOfSlug(stages, s.placementSlug);
  // Stages that own at least one placement this template actually carries.
  // A stage listing slugs the template dropped must not be able to block
  // add-to-cart forever.
  const liveStages = (stages ?? []).filter((st) =>
    st.slugs.some((sl) => config.placements.some((p) => p.slug === sl)),
  );
  const unfinishedStage =
    liveStages.find((st) => spotsForStage(st).length === 0) ?? null;
  // Stage-owned custom fields (placement "in_stage"). A garment's own colour
  // belongs in that garment's block, never in a shared strip below the list.
  // Resolved against the template's live field set so a slug the template
  // dropped is skipped rather than blocking add-to-cart forever. Conditional
  // rules are honoured here so a hidden field can never gate the button, the
  // same invariant pricing.ts applies when emitting the property.
  const fieldsForStage = (st: Stage): CustomField[] =>
    (st.fieldSlugs ?? [])
      .map((slug) => (config.customFields ?? []).find((f) => f.slug === slug))
      .filter(
        (f): f is CustomField => !!f && f.placement === "in_stage",
      )
      .filter((f) => evaluateCondition(f.conditionalRule, fieldValues));
  /** "2. The cap" -> "the cap", so it reads inside a sentence. */
  const stageLabel = (st: Stage) =>
    st.title.replace(/^\s*\d+\.\s*/, "").replace(/^./, (c) => c.toLowerCase());
  // The first REQUIRED stage field the customer has not answered. Named
  // rather than boolean on purpose: the CTA label and the blocked-tap scroll
  // both need to say WHICH one, and a nameless blocker is exactly the defect
  // bbf1a59 fixed inside the spot editor.
  const firstMissingStageField: { stage: Stage; field: CustomField } | null =
    (() => {
      for (const st of liveStages) {
        for (const f of fieldsForStage(st)) {
          if (!f.required) continue;
          const v = fieldValues[f.slug];
          const unset =
            v === undefined ||
            (typeof v === "string" && v.trim() === "") ||
            (Array.isArray(v) && v.length === 0);
          if (unset) return { stage: st, field: f };
        }
      }
      return null;
    })();
  // Which slugs the editor may offer. Editing an existing spot derives the
  // stage from the spot itself, so re-opening the cap does not present hoodie
  // placements just because activeStageKey went stale.
  // Which stage block the open editor belongs in.
  const editorStageKey: string | null = (() => {
    if (!stages) return null;
    // The draft's own placement wins whenever it has one. This covers editing
    // a saved spot AND a draft restored from a previous visit, where
    // activeStageKey is empty because component state does not survive a page
    // load.
    if (draft?.placementSlug) {
      const st = stageOfSlug(stages, draft.placementSlug);
      if (st) return st.key;
    }
    if (activeStageKey) return activeStageKey;
    // Never return null while the editor is open: on a staged template the
    // editor is ONLY rendered from inside a stage, so a null key renders it
    // nowhere and the customiser looks completely dead. That is exactly what
    // happened on 2026-07-22 when a restored draft reopened the editor after a
    // reload. Fall back to the first stage rather than vanishing.
    return liveStages[0]?.key ?? stages[0]?.key ?? null;
  })();
  const editorAllowedSlugs: string[] | null = (() => {
    if (!stages) return null;
    if (editingSpotId !== null && draft?.placementSlug) {
      const st = stageOfSlug(stages, draft.placementSlug);
      if (st) return st.slugs;
    }
    const st = activeStageKey
      ? stages.find((s) => s.key === activeStageKey)
      : null;
    return st ? st.slugs : null;
  })();
  // Phase 81: split the gate (see above). On a staged template the design is
  // only "finished" once EVERY garment has a spot. Without this the stages are
  // cosmetic: the old gate was spots.length > 0, so a customer could pay $239
  // for the Father's Day box having only designed the hoodie, leaving the cap
  // and socks blank with nothing to stitch.
  const bundleSectionVisible =
    spots.length > 0 &&
    !!ctx.variantId &&
    approvalChoice !== null &&
    unfinishedStage === null;
  // RK26682 (2026-09-17): an OPEN spot editor blocks add-to-cart. The cart
  // payload is built from SAVED spots only, so a photo the customer had just
  // swapped in the editor (visible in the thumbnail, the preview and the
  // running total) was silently dropped when she tapped Add to cart without
  // pressing "Update spot" first. Three shirts, three different photos, one
  // design stitched on all of them. Refuse and guide to Save / Update
  // instead: what she sees is then always what she adds.
  const editorBlock = atcEditorBlock({
    draftOpen: editorVisible,
    savedSpotCount: spots.length,
    editingExisting: editingSpotId !== null,
  });
  const draftReadyToSave =
    draft !== null && isDraftComplete(draft, config ?? undefined);
  const canAddToCart =
    bundleSectionVisible &&
    !editorBlock.blocked &&
    firstUnpickedBundleMember === null &&
    // Deliberately on canAddToCart and NOT on bundleSectionVisible: the
    // colour is picked up in the stage block, so hiding the bundle section
    // while it is missing would hide the running total for no reason.
    firstMissingStageField === null;
  // Wave 1 funnel: render-phase mirror feeding the dep-less rk_atc_shown
  // effect above (same pattern as helperProgressRef).
  canAddToCartLiveRef.current = canAddToCart;
  // Phase 81c (fixed 2026-07-02): CUSTOMER-VISIBLE item count for the CTA
  // label. 1 garment + every included bundle member. The hidden embroidery
  // surcharge line is NOT counted - it merges into the garment line via
  // cart-transform, so counting it made the button say "Add 2 items to
  // cart" for a single garment and then the cart showed 1 line.
  const includedBundleCount = (config.bundleMembers ?? []).filter(
    (m) => !m.optional || selectedBundleIds.has(m.id),
  ).length;
  const atcItemCount = 1 + includedBundleCount;

  // ── Spot lifecycle ─────────────────────────────────────────────────────

  // Shared close sequence: commit any pending state change to spots[],
  // then run the collapse animation + page scroll in parallel. The
  // newly-saved tile is mounted into the DOM BEFORE the scroll starts so
  // the scroll lands directly on it.
  function closeAndScroll(commit: () => void) {
    commit();
    // Double-rAF before launching the scroll. The first rAF lets React's
    // commit + layout pass finish (so refs are set and positions reflect
    // the new state). The second rAF gives the browser one paint pass so
    // getBoundingClientRect reflects the newly-mounted saved tile.
    //
    // We scroll to the SECTION (not the saved-spots div) and land its
    // heading ~1/3 down from the viewport top, so the customise section
    // sits in the upper-middle band of the screen rather than slammed
    // against the top. The customer wants the section centred in their
    // visual focus, with breathing room above.
    // Staged templates get NO scroll here at all. The old behaviour eased
    // to the top of the whole customise section, which after saving the cap
    // or socks jumped the customer far UP past finished stages, then the
    // 700ms collapse shifted the page again under the landing. That was the
    // heart of Oscar's "lands somewhere wrong, you have to go back down"
    // iPhone report. Instead, once the collapse completes and the layout is
    // final, nudge the NEXT unfinished stage into view only if it is fully
    // off-screen, with a native nearest-block scroll (small, one-shot, and
    // against settled geometry).
    const staged = !!stagesFor(config?.templateTag);
    if (!staged) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const target = embroideryRef.current;
          const offset =
            typeof window !== "undefined"
              ? Math.max(120, Math.round(window.innerHeight / 3))
              : 120;
          easedScrollTo(target, offset);
        });
      });
    }
    // Start the height/opacity collapse on the editor wrap. When it
    // lands, drop the draft state so React unmounts the (now invisible)
    // editor node.
    easedCollapse(() => {
      setDraft(null);
      setEditingSpotId(null);
      if (staged) {
        // After the unmount's layout pass: is the next stage visible?
        requestAnimationFrame(() => {
          const next = rootRef.current?.querySelector<HTMLElement>(
            '.rk-pc-stage[data-done="0"]',
          );
          if (!next) return;
          const r = next.getBoundingClientRect();
          const vh = window.innerHeight || 800;
          const fullyOffScreen = r.bottom <= 0 || r.top >= vh;
          if (fullyOffScreen) {
            next.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        });
      }
    });
  }

  function saveSpot() {
    if (!draft || !isDraftComplete(draft, config ?? undefined)) return;
    // Abandonment recovery: the design token is minted at the FIRST spot
    // save (same `rk-` format add-to-cart used to mint) so the server-side
    // draft and the eventual cart line share one identity. Re-saves reuse
    // it via the persisted envelope; add-to-cart falls back to minting
    // only when no spot save ever ran this session.
    const gid = designGroupId ?? mintGroupId();
    if (!designGroupId) setDesignGroupId(gid);
    closeAndScroll(() => {
      if (editingSpotId) {
        // Update in place. Preserve the spot's id + its position in the
        // list.
        const id = editingSpotId;
        setSpots((prev) =>
          prev.map((s) => (s.id === id ? { ...draft, id } : s)),
        );
      } else {
        setSpots((prev) => [...prev, { ...draft, id: nextSpotId() }]);
      }
    });
    // Phase 35h: emit a spot_saved event with the placement + mode + any
    // upsell tagged on the spot, so analytics can roll up "which spots
    // get added most" + "which upsells convert".
    emitAnalytics("spot_saved", {
      productGid: ctx.productGid,
      variantId: ctx.variantId,
      placementSlug: draft.placementSlug,
      mode: draft.mode,
      spotIndex: spots.length,
      upsellId: draft.addonUpsellId ?? undefined,
    });
    // A saved spot means a real design exists. Logged-in shoppers already
    // have an email, so fire the abandoned-design recovery signal now
    // (deduped). Guests fire it from the save-design card instead.
    // setSpots above hasn't flushed yet, so pass the post-save count
    // explicitly rather than reading the stale spots.length.
    if (ctx.customerEmail) {
      fireDesignStarted(
        ctx.customerEmail,
        editingSpotId ? spots.length : spots.length + 1,
        gid,
      );
      // Server draft save for logged-in shoppers, armed at the same
      // moment the recovery signal fires. The first POST flushes from
      // the persist effect - right after the just-saved spot commits -
      // so the server copy never misses the newest spot.
      armDraftSync(ctx.customerEmail, "logged_in", false);
    }
    if (draft.addonUpsellId) {
      const upsell = config?.upsells?.find((u) => u.id === draft.addonUpsellId);
      if (upsell) {
        emitAnalytics("upsell_picked", {
          productGid: ctx.productGid,
          upsellId: upsell.id,
          upsellKind: upsell.kind,
          upsellLabel: upsell.label,
          upsellPriceCents: upsell.priceCents,
          placementSlug: draft.placementSlug,
        });
      }
    }
  }

  function cancelDraft() {
    // No commit, just close + scroll.
    closeAndScroll(() => {});
  }

  // Abandoned-design recovery: fire a server-side "Started Custom Design"
  // Klaviyo event ONCE per session+product, the moment we have a real
  // design AND a known email (logged-in OR guest-captured). Deduped via a
  // ref + a per-product sessionStorage flag so re-renders / extra spots
  // never re-fire. Best-effort + fail-open: a Klaviyo/admin/DB hiccup is
  // invisible here and never blocks the design flow.
  function fireDesignStarted(
    email: string,
    spotCount?: number,
    draftId?: string | null,
  ) {
    if (!KLAVIYO_RECOVERY_ENABLED) return;
    if (!email || typeof window === "undefined") return;
    if (startedFiredRef.current) return;
    const flagKey = `rk-pc:kla-started:${ctx.productGid}`;
    try {
      if (window.sessionStorage.getItem(flagKey)) {
        startedFiredRef.current = true;
        return;
      }
      window.sessionStorage.setItem(flagKey, "1");
    } catch {
      // sessionStorage blocked — the ref guard still prevents a re-fire
      // within this mount.
    }
    startedFiredRef.current = true;
    // First artwork on the design, for the recovery email's thumb. At a
    // spot save the just-saved spot is still in `draft` (setSpots hasn't
    // flushed), so the open draft is checked too.
    const thumbKey =
      spots.find((s) => s.uploadStagingKey)?.uploadStagingKey ??
      draft?.uploadStagingKey ??
      null;
    trackDesignStarted(ctx.workerUrl, {
      email,
      productGid: ctx.productGid,
      productHandle: ctx.productHandle,
      templateTag: config?.templateTag ?? null,
      templateName: config?.templateSlug ?? null,
      spotCount: spotCount ?? Math.max(1, spots.length),
      pageUrl: window.location.href,
      draftId: draftId ?? designGroupId ?? null,
      thumbUrl: thumbKey ? uploadedFileUrl(ctx.workerUrl, thumbKey) : null,
    });
  }

  // ── Abandonment recovery: server draft sync ────────────────────────────
  // Once an email is known (guest card consent, or a logged-in spot save)
  // the persisted-state envelope is mirrored into the admin's BuilderDraft
  // table via the worker's /api/save-draft, keyed by the design token, so
  // the Klaviyo recovery email can rebuild the design on any device. The
  // first POST fires immediately; after that, every state change marks a
  // dirty flag and a 12s debounce collapses edit bursts into one POST.
  // All of it is best-effort and invisible to the customer.

  function sendDraftSaveNow() {
    const sync = draftSyncRef.current;
    const state = persistedStateRef.current;
    const gid = state?.groupId;
    if (!sync || !state || !isValidGroupId(gid)) return;
    draftSyncDirtyRef.current = false;
    const thumbKey =
      state.spots.find((s) => s.uploadStagingKey)?.uploadStagingKey ??
      state.draft?.uploadStagingKey ??
      null;
    void saveDraft(ctx.workerUrl, {
      id: gid,
      email: sync.email,
      productGid: ctx.productGid,
      productHandle: ctx.productHandle,
      templateTag: config?.templateTag ?? null,
      // productGid rides INSIDE the envelope too - the resume guard uses
      // it to refuse hydrating this design onto a different PDP.
      state: { ...state, productGid: ctx.productGid },
      thumbUrl: thumbKey ? uploadedFileUrl(ctx.workerUrl, thumbKey) : null,
      source: sync.source,
    }).then((ok) => {
      // A failed save re-arms the "first save" path so the next state
      // change retries immediately instead of silently never syncing.
      if (!ok && draftSyncRef.current) draftSyncRef.current.sentFirst = false;
    });
  }

  function scheduleDraftSave() {
    const sync = draftSyncRef.current;
    if (!sync) return;
    if (!sync.sentFirst) {
      sync.sentFirst = true;
      sendDraftSaveNow();
      return;
    }
    if (draftSyncTimerRef.current != null) return;
    draftSyncTimerRef.current = window.setTimeout(() => {
      draftSyncTimerRef.current = null;
      if (draftSyncDirtyRef.current) sendDraftSaveNow();
    }, DRAFT_SYNC_DEBOUNCE_MS);
  }

  // Arm (or re-email) the sync. flushNow pushes the first save right away
  // - used at the guest consent moment, where no state change is coming
  // to trigger the persist effect. Callers whose moment IS a state change
  // (spot save, resume hydration) pass false and let the effect flush.
  function armDraftSync(
    email: string,
    source: DraftSyncSource,
    flushNow: boolean,
  ) {
    if (!KLAVIYO_RECOVERY_ENABLED) return;
    const clean = email.trim();
    if (!clean) return;
    if (!draftSyncRef.current) {
      draftSyncRef.current = { email: clean, source, sentFirst: false };
    } else {
      draftSyncRef.current.email = clean;
    }
    draftSyncDirtyRef.current = true;
    if (flushNow) scheduleDraftSave();
  }

  // Abandonment recovery: drive every setter the persisted-state
  // initializers read, in one place, so a ?rk_draft resume rebuilds the
  // exact same picture a sessionStorage rehydrate would. (approvalChoice
  // is normalised back to "skip" by the [spots] reset effect, matching
  // what a plain reload does.)
  function applyRestoredState(state: PersistedState) {
    setSpots(Array.isArray(state.spots) ? state.spots : []);
    setDraft(state.draft ?? null);
    setApprovalChoice(state.approvalChoice ?? "skip");
    setApprovalAddonId(state.approvalAddonId ?? null);
    setCustomerNotes(state.customerNotes ?? "");
    setOrderAddOnIds(
      Array.isArray(state.orderAddOnIds) ? state.orderAddOnIds : [],
    );
    setFieldValues(
      state.fieldValues && typeof state.fieldValues === "object"
        ? state.fieldValues
        : {},
    );
    setSelectedBundleIds(
      new Set(
        Array.isArray(state.selectedBundleIds) ? state.selectedBundleIds : [],
      ),
    );
    setBundleMemberVariantPicks(
      state.bundleMemberVariantPicks &&
        typeof state.bundleMemberVariantPicks === "object"
        ? state.bundleMemberVariantPicks
        : {},
    );
    // The draft id becomes this design's group id, so an add-to-cart
    // after the resume marks the BuilderDraft converted.
    if (isValidGroupId(state.groupId)) setDesignGroupId(state.groupId);
  }

  // Wave 1: state-only editor open, shared by the user-tapped buttons
  // (via startNewSpot) and the fresh-load auto-open effect, which must
  // NOT emit rk_design_started (see pendingAutoStartRef).
  function openNewSpot() {
    setEditingSpotId(null);
    setOpenSection("customise");
    // The placement is intentionally NOT pre-selected: the customer picks
    // their first embroidery location themselves (the picker opens first).
    // The CRO 3.1 tap-tax win is preserved by one-tap select-and-advance,
    // not by pre-picking a placement they didn't choose.
    const fresh = emptyDraft();
    // Phase 36 / 70: on pet/kid/person templates, every spot uses the
    // dedicated flow (no mode picker). Pre-lock mode=logo + bind the
    // flow's design upsell so the upload step renders immediately. The
    // upsell is now resolved from the bound FlowBlueprint instead of a
    // magic kind string.
    const flowUpsell = designUpsellFor(config);
    if (flowUpsell) {
      fresh.mode = "logo";
      fresh.addonUpsellId = flowUpsell.id;
      fresh.addonCount =
        flowUpsell.countMax && flowUpsell.countMax > 1 ? 1 : null;
    }
    setDraft(fresh);
  }

  // Funnel: the customer started designing. spotIndex 0 = first design,
  // higher = "+ Add another spot". Fills the view -> started -> saved
  // -> add-to-cart funnel between template_view and spot_saved.
  function emitDesignStartedEvent() {
    emitAnalytics("design_started", {
      productGid: ctx.productGid,
      productHandle: ctx.productHandle,
      variantId: ctx.variantId,
      spotIndex: spots.length,
      templateTag: config?.templateTag ?? undefined,
      templateName: config?.templateSlug ?? undefined,
      // Identify logged-in shoppers to Klaviyo onsite (best-effort, in
      // analytics.ts). Empty for guests until they use the save-design card.
      customerEmail: ctx.customerEmail || undefined,
    });
  }

  function openNewSpotScoped(stageKey: string | null) {
    setActiveStageKey(stageKey);
    openNewSpot();
    // An explicit tap IS the start signal - fire now and clear any
    // deferred auto-open emission so it can't double-fire later.
    pendingAutoStartRef.current = false;
    emitDesignStartedEvent();
  }
  // Deliberately takes no argument: it is passed straight to onClick in the
  // unstaged path, so a positional param would receive the MouseEvent.
  function startNewSpot() {
    openNewSpotScoped(null);
  }
  /** Open the editor scoped to one garment stage (see ./stages.ts). */
  function startStageSpot(stageKey: string) {
    openNewSpotScoped(stageKey);
  }
  // Editor markup, so a staged template renders it INSIDE the stage block the
  // customer tapped rather than once below the whole list.
  function renderEditor() {
    if (!editorVisible || !config) return null;
    return (
      <div className="rk-pc-editor-wrap" ref={editorWrapRef}>
        <PlacementSection
          config={config}
          workerUrl={ctx.workerUrl}
          customerEmail={ctx.customerEmail}
          draft={draft!}
          onChange={setDraft}
          onSave={saveSpot}
          onCancel={cancelDraft}
          lockedSlugs={lockedSlugs}
          priorLogos={priorLogos}
          isEditingExisting={editingSpotId !== null}
          isAdditionalSpot={spots.length > 0 && editingSpotId === null}
          hasPriorSpots={spots.length > 0}
          onPlacementPicked={handlePlacementPicked}
          allowedSlugs={editorAllowedSlugs}
        />
      </div>
    );
  }

  // Starter examples removed 2026-07-25 (Oscar): the strip confused more
  // than it converted. StarterStrip.tsx + the starter_picked analytics
  // mapping remain for a potential return; nothing renders them.

  // Wave 1 funnel: first placement-card tap inside the editor (fired at
  // most once per editor session by PlacementSection; never fired by the
  // single-placement auto-pick). Flushes the deferred design_started from
  // the auto-open first so the funnel keeps its ordering.
  function handlePlacementPicked(slug: string) {
    if (pendingAutoStartRef.current) {
      pendingAutoStartRef.current = false;
      emitDesignStartedEvent();
    }
    emitAnalytics("placement_picked", {
      productGid: ctx.productGid,
      productHandle: ctx.productHandle,
      variantId: ctx.variantId,
      placementSlug: slug,
      spotIndex: spots.length,
      flowKind: flowKindFor(config),
    });
  }

  // Customer added their design to the cart and wants to build another
  // (e.g. a second pet, or the same pet on a different garment). Wipe the
  // customiser back to a clean slate, drop the persisted snapshot, and
  // glide back to the top so they start fresh from the Reuse section.
  /** The field map a fresh design starts from: admin defaults only, never the
   *  previous design's picks. `clearPersisted` alone does not achieve this,
   *  the persist effect lists fieldValues as a dependency and re-writes the
   *  whole envelope the moment `spots` next changes, so box two would inherit
   *  box one's cap and sock colours and the ATC gate would read them as
   *  answered without the customer ever seeing a swatch. */
  function freshFieldValues(): Record<string, string | string[]> {
    const out: Record<string, string | string[]> = {};
    for (const f of config?.customFields ?? []) {
      if (!f.defaultValue) continue;
      out[f.slug] =
        f.fieldType === "checkboxes"
          ? f.defaultValue.split(",").map((s) => s.trim()).filter(Boolean)
          : f.defaultValue;
    }
    return out;
  }

  /** Drop only the stage-owned picks (cap colour, sock colour), leaving
   *  order-level fields alone. Used where the design changes but the order
   *  does not. */
  function clearStageFieldValues() {
    const owned = new Set(
      (stagesFor(config?.templateTag) ?? []).flatMap((s) => s.fieldSlugs ?? []),
    );
    if (owned.size === 0) return;
    setFieldValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const slug of owned) {
        if (slug in next) {
          delete next[slug];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }

  function startOver() {
    setSpots([]);
    setDraft(null);
    setEditingSpotId(null);
    setApprovalChoice("skip");
    setApprovalAddonId(null);
    setOrderAddOnIds([]);
    setCustomerNotes("");
    setFieldValues(freshFieldValues());
    setAdded(false);
    setErr(null);
    // A fresh design gets a fresh token (minted at its first spot save).
    setDesignGroupId(null);
    setPreviewOpen(false);
    setOpenSection("reuse");
    // C7: clear both the session + local copies so a recovered draft never
    // re-hydrates on top of the fresh flow.
    clearPersisted(ctx.productGid);
    // The design they just added is now a reuse option too.
    void refreshCart();
    // No scrollIntoView here (removed 2026-07-24): setOpenSection("reuse")
    // above flips ReuseSection's useOpenScroll, which produces the section
    // scroll through the shared guarded path. The extra block:"start" jump
    // here raced it, two competing scrolls in one tap.
  }

  function editSpot(id: string) {
    const s = spots.find((x) => x.id === id);
    if (!s) return;
    const { id: _id, ...rest } = s;
    setDraft({ ...rest });
    setEditingSpotId(id);
  }

  function removeSpot(id: string) {
    const removed = spots.find((s) => s.id === id);
    setSpots((prev) => prev.filter((s) => s.id !== id));
    // If they removed the spot they were editing, drop the draft too so we
    // don't accidentally re-save it.
    if (editingSpotId === id) {
      setDraft(null);
      setEditingSpotId(null);
    }
    emitAnalytics("spot_removed", {
      productGid: ctx.productGid,
      variantId: ctx.variantId,
      placementSlug: removed?.placementSlug ?? null,
      mode: removed?.mode ?? null,
    });
  }

  // Customer picked a previous design from the Reuse section. Open the
  // editor with it pre-populated as a fresh draft (a brand new spot; we
  // don't overwrite anything they were already editing).
  //
  // A reused design is the SAME artwork as the original — it was already
  // digitised (and paid for) on the source cart line / past order. Mark
  // it `uploadAlreadyDigitised` so pricing waives the digitisation fee /
  // the pet/kid scan upsell — the artwork is only ever digitised once.
  // 2026-07-25: reuse sources are CROSS-PRODUCT. The recent-designs list is
  // one global localStorage key and the past-order lookup is not scoped to
  // this template, so a design saved on (say) the Best Dad cap can be
  // reused onto a hoodie. Two things then travel that do not belong here:
  //
  //   placementSlug - slugs differ per garment (`hat-back`, `centre` vs
  //     `centre-front`, `l-chest` vs `left-chest`). A foreign slug resolved
  //     to no placement, which hid every editor step, auto-confirmed the
  //     placement step, still let Save enable, and then priced the spot at
  //     $0 because the pricing loop skips spots whose placement is unknown.
  //   addonUpsellId - the pet/kid/person design upsell may not exist on
  //     this template, in which case the whole add-on block (including the
  //     $45 coloured + filled surcharge) silently evaporated.
  //
  // Dropping both makes the customer answer this template's own questions,
  // which is the only honest outcome. Deliberately NOT substituting this
  // template's upsell: that would bill someone for an add-on they never
  // picked.
  function sanitiseForeignDraft(
    picked: EmbroideryDraft,
  ): Partial<EmbroideryDraft> {
    const patch: Partial<EmbroideryDraft> = {};
    const slugKnown =
      !!picked.placementSlug &&
      (config?.placements ?? []).some((p) => p.slug === picked.placementSlug);
    if (picked.placementSlug && !slugKnown) {
      patch.placementSlug = null;
      patch.sizeMm = undefined;
    }
    const addonKnown =
      !!picked.addonUpsellId &&
      (config?.upsells ?? []).some((u) => u.id === picked.addonUpsellId);
    if (picked.addonUpsellId && !addonKnown) {
      patch.addonUpsellId = null;
      patch.addonCount = null;
      patch.addonStyleColoured = false;
      patch.addonStyleSingleLine = false;
      // 2026-08-14: pre-RK24457 orders shipped with a contradiction — a
      // LINE-style add-on alongside "match-design" thread codes the old
      // logo flow offered unconditionally. Same-product reuse keeps the
      // add-on, so the RK24457 strip effect clears the stale sentinel;
      // on THIS cross-product path the add-on is dropped and that effect
      // never fires, and "Match the design" now bills $45 — for artwork
      // that was digitised as a single-colour line drawing. A COLOURED
      // source keeps its sentinel (the fee is per-garment stitch work,
      // RK24263); only the line-style contradiction is stripped.
      if (!picked.addonStyleColoured) {
        const strip = (codes: string[] | undefined) =>
          (codes ?? []).filter((c) => c !== MATCH_DESIGN_CODE);
        patch.logoThreadCodes = strip(picked.logoThreadCodes);
        patch.textThreadCodes = strip(picked.textThreadCodes);
      }
    }
    return patch;
  }

  function pickReuse(picked: EmbroideryDraft) {
    setEditingSpotId(null);
    setOpenSection("customise");
    // Upload-later ghost: a reused spot whose artwork never arrived has
    // nothing digitised - it re-opens as a fresh upload-later spot that
    // bills normally (fee waiver here would zero the scan upsell too).
    const artworkPending = !!picked.uploadLater && !picked.uploadStagingKey;
    setDraft({
      ...picked,
      ...sanitiseForeignDraft(picked),
      uploadAlreadyDigitised: !artworkPending,
    });
    // A reused design carries artwork, not garment choices: ReusableDesign has
    // no channel for field values. Clear the stage-owned picks so this box
    // gets its own colours instead of silently inheriting the last one's.
    clearStageFieldValues();
    // Intentionally no scrollIntoView. Feels jarring on the customer's
    // side. The editor mounts in place; if they need to scroll they will.
  }

  // Phase 31: customer ticked a verified past-order proof PDF. We've
  // already ingested it into R2 via /api/ingest-proof and have a fresh
  // stagingKey. Open (or modify) a draft with the proof as the logo.
  // If a draft is already open (e.g. customer was mid-edit), we attach
  // the proof to that draft. Otherwise we create a fresh logo-mode
  // draft with the template's default placement, so the customer can
  // dive straight into sizing/threads.
  function pickProof(ingested: {
    stagingKey: string;
    contentType: string;
    fileHash: string;
    filename: string;
    sourceOrderName: string;
  }) {
    setEditingSpotId(null);
    setOpenSection("customise");
    setDraft((d) => {
      // No placement is pre-selected; the customer picks their first
      // location (or keeps the one from a draft already in progress).
      const base = d ?? emptyDraft();
      return {
        ...base,
        // Force mode to logo on first pick — the proof IS the logo. If
        // the customer was on "text" + ticks a proof, that's a meaningful
        // signal they want to add a logo to the mix.
        mode: base.mode === "text" ? "both" : "logo",
        uploadStagingKey: ingested.stagingKey,
        uploadFilename: ingested.filename,
        uploadMimeType: ingested.contentType,
        uploadFileHash: ingested.fileHash,
        uploadAlreadyDigitised: true,
        // Clear any legacy-upload URL ref since we now have a real file.
        legacyUploadUrl: null,
        // Phase 67i: remember which past order this design came from so
        // production sees the link on the new order. propertiesFor emits
        // a visible "Spot N reused from <order>" row when this is set.
        sourceOrderName: ingested.sourceOrderName,
      };
    });
    emitAnalytics("proof_picked", {
      productGid: ctx.productGid,
      productHandle: ctx.productHandle,
      variantId: ctx.variantId,
    });
  }

  // ── Wave 1: blocked-CTA guidance ───────────────────────────────────────

  // Find the surface that's actually blocking add-to-cart and ease the
  // page to it: the open substep in the draft editor, then the editor
  // itself, then the customise section (no spots yet), then the bundle
  // blocker strip, then (no variant picked) the top of the customiser,
  // nearest the theme's size picker.
  function scrollToBlocking() {
    const root = rootRef.current;
    if (!root) return;
    const target =
      // RK26682: a finished but unsaved edit is the blocker. The next action
      // is the Save / Update button, not the top of the editor.
      (editorBlock.blocked && spots.length > 0 && draftReadyToSave
        ? root.querySelector<HTMLElement>(".rk-pc-editor-actions")
        : null) ??
      root.querySelector<HTMLElement>(".rk-pc-substep.is-open") ??
      (editorVisible ? editorWrapRef.current : null) ??
      (spots.length === 0 ? embroideryRef.current : null) ??
      // An unanswered stage field (the cap / sock colour). Sits above the
      // bundle blocker because it lives further up the page, inside its own
      // garment block.
      (firstMissingStageField
        ? root.querySelector<HTMLElement>(
            `[data-rk-field-slug="${firstMissingStageField.field.slug}"]`,
          )
        : null) ??
      // A whole garment still to do ("Finish the socks"). Below the missing
      // colour field on purpose: the field lives further up, inside its own
      // stage block, and must win when both are outstanding.
      (unfinishedStage
        ? root.querySelector<HTMLElement>(
            `[data-stage-key="${unfinishedStage.key}"]`,
          )
        : null) ??
      (firstUnpickedBundleMember
        ? root.querySelector<HTMLElement>(".rk-pc-bundle-blocker")
        : null) ??
      (!ctx.variantId ? rootRef.current : null) ??
      embroideryRef.current;
    easedScrollTo(target, Math.round((window.innerHeight || 800) * 0.3));
  }

  // Tap on the visually-disabled ATC: guide instead of dead-clicking.
  // Scrolls to the blocking substep and pulses the "Still to do" list
  // (class toggle; the animation lives in styles.css). Never submits.
  function guideBlockedAtc() {
    if (submitting) return;
    scrollToBlocking();
    // Prefer the unanswered stage field: when the blocker is a colour the
    // editor is closed, so ".rk-pc-editor-missing" is not in the DOM at all
    // and the tap would pulse nothing.
    const missing =
      (firstMissingStageField
        ? rootRef.current?.querySelector<HTMLElement>(
            `[data-rk-field-slug="${firstMissingStageField.field.slug}"]`,
          )
        : null) ??
      rootRef.current?.querySelector<HTMLElement>(".rk-pc-editor-missing");
    if (missing) {
      missing.classList.remove("is-pulsing");
      // Force a reflow so a rapid re-tap restarts the animation.
      void missing.offsetWidth;
      missing.classList.add("is-pulsing");
      window.setTimeout(() => missing.classList.remove("is-pulsing"), 1800);
    }
    // RK26682: when the blocker is an edit that is complete but not saved
    // there is no "Still to do" list to pulse, so pulse the Save / Update
    // button itself.
    if (!missing && editorBlock.blocked && draftReadyToSave) {
      const saveBtn = rootRef.current?.querySelector<HTMLElement>(
        ".rk-pc-editor-actions .rk-pc-cta:not(.rk-pc-cta--ghost)",
      );
      if (saveBtn) {
        saveBtn.classList.remove("is-pulsing");
        void saveBtn.offsetWidth;
        saveBtn.classList.add("is-pulsing");
        window.setTimeout(() => saveBtn.classList.remove("is-pulsing"), 1800);
      }
    }
  }

  // Wave 1: tap on the section-header price chip - open the "What's in
  // this price?" breakdown at the foot and glide to it. Falls back to
  // the item-total row when the breakdown has nothing to show yet.
  function openPriceBreakdown() {
    const root = rootRef.current;
    if (!root) return;
    const details = root.querySelector<HTMLDetailsElement>(
      "details.rk-pc-breakdown",
    );
    if (details) {
      details.open = true;
      easedScrollTo(details, Math.round((window.innerHeight || 800) * 0.3));
      return;
    }
    const totalRow = root.querySelector<HTMLElement>(".rk-pc-total-row");
    if (totalRow) {
      easedScrollTo(totalRow, Math.round((window.innerHeight || 800) * 0.3));
    }
  }

  // ── Cart submit ────────────────────────────────────────────────────────
  async function onAddToCart() {
    if (!canAddToCart || !ctx.variantId) return;
    // Wave 1 funnel: the tap itself, BEFORE any network work, so a
    // drop-off between tap and success is measurable (distinct from
    // add_to_cart_success below).
    emitAnalytics("atc_clicked", {
      productGid: ctx.productGid,
      productHandle: ctx.productHandle,
      variantId: ctx.variantId,
      spotCount: spots.length,
      totalCents,
      currency: ctx.currency,
      flowKind: flowKindFor(config),
    });
    setSubmitting(true);
    setAtcError(null);
    try {
      // Phase 67v — 2-line cart add. A single repriced line needs a Cart
      // Transform Function, unavailable on this non-Plus plan (custom-app
      // functions don't run; the Easify piggyback is dead). So the
      // customisation upcharge rides on a SEPARATE "Embroidery
      // customisation" line. Both go in ONE atomic /cart/add.js call so a
      // garment can never land in the cart unpriced.
      //
      // Shopify/Rebuy render the most-recently-added line FIRST, so the
      // garment is added LAST — that keeps the garment line ABOVE its
      // "Embroidery customisation" line(s) in the cart.
      //
      // Abandonment recovery: the token is minted at the FIRST spot save
      // now (it doubles as the server-side BuilderDraft id), so a normal
      // flow reuses the stored one here and the admin can stamp the
      // draft converted. The mint fallback covers legacy persisted
      // sessions + corrupt storage - byte-identical to the old inline
      // mint this line used to do.
      const groupId = reuseOrMintGroupId(designGroupId);
      const props = propertiesFor(
        spots,
        config!,
        approvalChoice,
        customerNotes,
        approvalAddonId,
        orderAddOnIds,
        ctx.workerUrl,
        fieldValues,
      );
      props["_rk_group_id"] = groupId;

      // Persist the full machine-readable property bag server-side so the
      // cart line only has to carry the human-readable rows + the
      // `_rk_group_id` token — Shopify shows every line-item property in
      // the admin order page, so the underscore-prefixed bag would
      // otherwise clutter every order. On success the garment line gets
      // the lean property set; on failure we fall back to the full bag on
      // the line so the order is still fully processable (just cluttered)
      // — the orders/create webhook resolves either shape.
      // 2026-07-03: sold-out pre-order detection. Keyed on ctx.variantId -
      // the size the customer picked, sees, and is PRICED on (garmentPriceCents
      // is fetched for ctx.variantId) - so the note, the 85% price, and the
      // carted line can never disagree. Computed BEFORE saveDesign so the
      // markers persist into BuilderDesign + the approvals handoff (the raw
      // line's props are otherwise dropped by resolveLineProperties).
      const preorderMap = config!.settings?.preorderVariantMap ?? {};
      const pickedVariant = (productJson?.variants ?? []).find(
        (v) => String(v.id) === String(ctx.variantId),
      );
      const variantIsOOS = pickedVariant?.available === false;
      // 2026-07-25: FULL price back-order (discount removed). floor() only
      // guards against a non-whole-dollar garment price - never overcharges.
      const preorderDollars =
        garmentPriceCents != null
          ? Math.floor(garmentPriceCents / 100)
          : null;
      const preorderVariantNumeric =
        preorderDollars != null && preorderDollars > 0
          ? preorderMap[String(preorderDollars)]
          : undefined;
      const isPreorder =
        PREORDER_ENABLED && variantIsOOS && preorderVariantNumeric != null;

      // A sold-out size we CAN'T turn into a pre-order (price not loaded yet,
      // map miss, feature off) must NOT fall through to adding the real
      // DENY variant - Shopify rejects the whole atomic /cart/add.js. Block
      // with a clear message instead of a raw cart error. Unconditional on
      // the flag: with pre-orders off, sold out means sold out.
      if (variantIsOOS && !isPreorder) {
        throw new Error(
          "That size is sold out - pick another size or colour.",
        );
      }

      const preorderSizeLabel =
        pickedVariant?.title || ctx.garmentColourName || "your size";
      // Real supplier next-delivery date for this size if we have a future one,
      // else the honest generic window.
      const preorderEtaCopy =
        formatPreorderEta(config?.settings?.variantEtaMap?.[String(ctx.variantId)]) ??
        PREORDER_ETA_COPY;
      if (isPreorder) {
        const sku = pickedVariant?.sku || "";
        const itemName =
          productJson?.title ||
          (ctx.productHandle
            ? ctx.productHandle.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
            : "Custom garment");
        // Hidden machine markers (persisted via saveDesign + on the line).
        props["_rk_preorder"] = "1";
        props["_rk_preorder_variant_id"] = String(ctx.variantId);
        props["_rk_preorder_eta"] = preorderEtaCopy;
        // VISIBLE (Shopify hides _-prefixed props on the order page + packing
        // slip) so the team sees what to make + which SKU to reorder.
        props["Back-order"] =
          `${itemName} - ${preorderSizeLabel} (ships when back in stock, ${preorderEtaCopy})`;
        if (sku) props["Back-order SKU"] = sku;
      }

      // Abandonment recovery: when an email is resolvable (guest gave one
      // via the save-design card, or the shopper is logged in) it rides
      // along so the admin can stamp the Klaviyo profile's
      // rk_last_design_* properties + fire "RK Design Added to Cart".
      // Guest emails only count once consent was given (guestSaved). The
      // cart line's property bag is untouched either way.
      // Gated on the recovery kill switch too, so flipping the switch is an
      // atomic launch: OFF means zero recovery-related Klaviyo writes (no
      // profile stamps, no events) even for logged-in shoppers.
      const knownEmail = !KLAVIYO_RECOVERY_ENABLED
        ? null
        : (guestSaved && isValidEmail(guestEmail) ? guestEmail.trim() : "") ||
          ctx.customerEmail ||
          null;
      const designSaved = await saveDesign(
        ctx.workerUrl,
        groupId,
        ctx.productGid,
        props,
        { email: knownEmail, productHandle: ctx.productHandle },
      );
      const garmentProps = designSaved ? leanCartProperties(props) : props;

      // Phase 73: variant binding. When any custom field has
      // fieldRole=variant_axis and the customer's pick maps to a real
      // variant on this product, override the cart line's variant id.
      // Falls back to ctx.variantId in every defensive case (no
      // variant_axis fields, productJson not loaded yet, incomplete
      // pick, no matching variant), so add-to-cart never breaks because
      // of a configurator gap.
      const resolvedVariantId =
        resolveVariantId(
          config!.customFields,
          fieldValues,
          productJson,
          ctx.variantId,
        ) ?? ctx.variantId;

      const garmentItem = {
        id: Number(resolvedVariantId),
        quantity: 1,
        properties: garmentProps,
      };

      // Pre-order markers must be on the LINE even if leanCartProperties
      // trimmed them from garmentProps, so checkout/order show them.
      const preorderLineMarkers: Record<string, string> = isPreorder
        ? {
            _rk_preorder: "1",
            _rk_preorder_variant_id: String(ctx.variantId),
            _rk_preorder_eta: preorderEtaCopy,
            "Back-order": String(props["Back-order"] ?? ""),
            ...(props["Back-order SKU"]
              ? { "Back-order SKU": String(props["Back-order SKU"]) }
              : {}),
          }
        : {};

      // Upload-later (2026-07-17): same survive-the-lean-split treatment.
      // The orders/create webhook keys the upload-pending tag + fulfilment
      // hold off this LINE property (no DB round-trip on the webhook path).
      // Mode-gated to mirror pricing.propertiesFor's emission exactly - a
      // stale uploadLater flag on a text spot must never assert a pending
      // upload the bag doesn't list (the hold would be unreleasable).
      const uploadLaterLineMarkers: Record<string, string> = spots.some(
        (s) =>
          (s.mode === "logo" || s.mode === "both") &&
          s.uploadLater &&
          !s.uploadStagingKey,
      )
        ? { _rk_upload_pending: "1" }
        : {};

      // The garment cart line(s): either the real variant, or the pre-order
      // stand-in at 85% carrying the design + pre-order markers.
      const garmentLines: Array<{
        id: number;
        quantity: number;
        properties: Record<string, string>;
      }> = isPreorder
        ? [
            {
              id: Number(preorderVariantNumeric),
              quantity: 1,
              properties: {
                ...garmentProps,
                ...preorderLineMarkers,
                ...uploadLaterLineMarkers,
              },
            },
          ]
        : [
            {
              ...garmentItem,
              properties: {
                ...garmentItem.properties,
                ...uploadLaterLineMarkers,
              },
            },
          ];

      // Surcharge line(s). The "Embroidery customisation" product carries
      // one exact-price variant per whole dollar ($1..$500). An upcharge
      // above the top variant splits into chunks — with the range at $500
      // that effectively never happens, so it's almost always one line.
      const surchargeItems: Array<{
        id: number;
        quantity: number;
        properties: Record<string, string>;
      }> = [];

      if (totalCents > 0) {
        const map = config!.settings?.surchargeVariantMap ?? {};
        const maxChunk = Object.keys(map).reduce(
          (m, k) => Math.max(m, Number(k) || 0),
          1,
        );
        let dollars = Math.round(totalCents / 100);
        const chunks: number[] = [];
        while (dollars > maxChunk) {
          chunks.push(maxChunk);
          dollars -= maxChunk;
        }
        if (dollars > 0) chunks.push(dollars);

        const surchargeProps = (): Record<string, string> => ({
          _added_by: "rk-builder-surcharge",
          _rk_group_id: groupId,
        });

        let mappedAll = true;
        for (const chunk of chunks) {
          const vid = map[String(chunk)];
          if (vid == null) {
            mappedAll = false;
            break;
          }
          surchargeItems.push({
            id: Number(vid),
            quantity: 1,
            properties: surchargeProps(),
          });
        }

        // Fallback: the exact-price map is missing a chunk — use the
        // legacy $1 variant at quantity = total dollars.
        if (!mappedAll) {
          const fallbackId = config!.settings?.surchargeVariantId;
          if (!fallbackId) {
            throw new Error(
              "Customisation pricing isn't set up yet. Please contact us before ordering.",
            );
          }
          surchargeItems.length = 0;
          surchargeItems.push({
            id: Number(fallbackId),
            quantity: Math.round(totalCents / 100),
            properties: surchargeProps(),
          });
        }
      }

      // Phase 74: bundle members. When the template wires extra
      // products, add one line per member sharing the same
      // _rk_group_id so the cart-transform can correlate them. Each
      // member uses its own variantGid (or its product's default
      // variant via /products/<handle>.js) and gets a propagation-
      // filtered subset of the design properties.
      const bundleItems: Array<{
        id: number;
        quantity: number;
        properties: Record<string, string>;
      }> = [];
      // Phase 75: only mandatory members (optional=false) AND optional
      // members the customer has ticked actually ride into the cart.
      const allMembers = config!.bundleMembers ?? [];
      const members = allMembers.filter(
        (m) => !m.optional || selectedBundleIds.has(m.id),
      );
      // Build the propagated design-props subset ONCE, outside the members
      // gate, and stash it with the group id: the post-ATC "complete the
      // set" card (SecondItemCard) adds members to this SAME group after
      // the main add, so production resolves one design for the whole set.
      // The construction itself lives in util/bundleMemberLine.ts, shared
      // with the card so the two add paths can never drift.
      const propagatedProps = buildPropagatedProps(props, config!.customFields);
      lastAtcRef.current = { groupId, propagatedProps };
      if (members.length > 0) {
        // Resolve each member's variant id. Prefer the admin-picked
        // variantGid; fall back to /products/<handle>.js's first
        // variant. Parallel fetches when needed so the user doesn't
        // wait sequentially on multiple round trips.
        const resolvedMembers = await Promise.all(
          members.map(async (m) => {
            let variantNumericId: string | null = null;
            // Phase 81: customer's inline pick has highest precedence.
            // canAddToCart already gates on every multi-variant member
            // having a pick, so we trust the value here.
            const pick = bundleMemberVariantPicks[m.id];
            if (pick && /^\d+$/.test(pick)) {
              variantNumericId = pick;
            }
            if (!variantNumericId && m.variantGid) {
              const tail = m.variantGid.split("/").pop();
              if (tail && /^\d+$/.test(tail)) variantNumericId = tail;
            }
            if (!variantNumericId && m.productHandle) {
              const j = await fetchProductJson(m.productHandle);
              const firstVariantId = j?.variants?.[0]?.id;
              if (firstVariantId != null) {
                variantNumericId = String(firstVariantId);
              }
            }
            return { member: m, variantNumericId };
          }),
        );
        for (const { member, variantNumericId } of resolvedMembers) {
          if (!variantNumericId) {
            console.warn(
              "[rk-builder] bundle member skipped (no variant id):",
              member.productHandle,
            );
            continue;
          }
          bundleItems.push({
            id: Number(variantNumericId),
            quantity: member.quantity,
            properties: buildMemberCartProps(member, groupId, propagatedProps),
          });
        }
      }

      // Garment LAST so it renders above the surcharge + bundle
      // line(s). Order: surcharge(s) -> bundle members -> primary
      // garment, so Shopify/Rebuy stack them with the primary on top
      // (most-recent-added renders first).
      const items = [...surchargeItems, ...bundleItems, ...garmentLines];
      await addItemsToCart(items);
      setAdded(true);
      if (isPreorder) {
        emitAnalytics("preorder_requested", {
          productGid: ctx.productGid,
          productHandle: ctx.productHandle,
          variantId: ctx.variantId,
        });
      }
      // Save the design(s) to the localStorage recent list, then refresh
      // the reusable-design list so what was just added is immediately
      // offered under "Reuse a previous design" — on this product and the
      // next — independent of how cart apps reshape the cart line.
      saveRecentDesigns(
        spots.map((s) => {
          const { id: _id, ...rest } = s;
          return rest;
        }),
        ctx.productHandle
          ? ctx.productHandle
              .replace(/-/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase())
          : "A previous design",
      );
      void refreshCart();
      // Phase 35h: emit success event so the storefront pixel listeners
      // can fire conversion tags. Includes spot count + total delta.
      emitAnalytics("add_to_cart_success", {
        productGid: ctx.productGid,
        productHandle: ctx.productHandle,
        variantId: ctx.variantId,
        spotCount: spots.length,
        totalCents,
        currency: ctx.currency,
        approvalChoice,
        approvalAddonId,
      });
      // Wave 1: the STANDARD GA4 ecommerce add_to_cart alongside the
      // custom success event, so GA4's built-in reports + audiences see
      // the conversion. Price = garment + embroidery in dollars.
      emitGa4AddToCart({
        variantId: ctx.variantId,
        itemName: productJson?.title ?? null,
        priceCents: (garmentPriceCents ?? 0) + totalCents,
        currency: ctx.currency,
      });
      // Phase 35e: the snapshot is cleared once the design is safely in
      // the cart (session + local copies). Audit 2026-10: that clear now
      // lives in the `added`-keyed effect next to the persist effect, NOT
      // inline here - the setDesignGroupId(null) below re-runs the persist
      // effect, and an inline clear was being overwritten by it in the
      // same commit. Customer can keep configuring (a fresh persistence
      // cycle starts on their next edit) but the original work is in the
      // cart.
      // The token now lives on the cart line - drop it so the NEXT design
      // (or a re-add of an edited one) mints its own and two cart lines
      // can never share a group id. Matches the old mint-per-ATC
      // behaviour exactly.
      setDesignGroupId(null);
      // Phase 67k: notify the theme + apps that the cart changed.
      // Easify used to fan this signal out for us; without it we have
      // to fire the events Rebuy / theme cart drawers listen for, plus
      // the modern Shopify Section Rendering ping. Fire ALL common
      // events on document + window — listeners pick up whichever name
      // their app uses.
      try {
        const cart = await fetch("/cart.js", { credentials: "same-origin" })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);
        const events = [
          "cart:updated",
          "cart:refresh",
          "cart:add",
          "cart-updated",
          "theme:cart:updated",
          "rebuy:cart:refresh",
          "rebuy:cart-updated",
        ];
        for (const name of events) {
          const detail = cart ? { cart } : undefined;
          document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
          window.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
        }
        // Open the cart drawer explicitly. The custom RKC drawer also
        // auto-opens via its /cart/add.js fetch interceptor; this direct
        // call is belt-and-braces. (Replaced the old Rebuy SmartCart hook
        // 2026-07-02 - Rebuy was uninstalled.)
        const rkCart = (window as unknown as { RKCart?: { open?: () => void } })
          .RKCart;
        rkCart?.open?.();
      } catch {
        // Non-fatal: cart-refresh broadcast is best-effort.
      }
    } catch (e) {
      // Inline, retryable. The design is intact; do NOT set the fatal
      // `err` (which would replace the whole customiser with the
      // config-unavailable banner and strand the finished design).
      const raw = (e as Error).message || "";
      // Some throws carry a customer-ready sentence (sold-out size,
      // pricing not configured); a bare "cart_add_failed_NNN" does not.
      const friendly = /^[a-z_]+_\d+$/.test(raw)
        ? "That didn't go through. Please tap Add to cart again."
        : raw ||
          "That didn't go through. Please tap Add to cart again.";
      setAtcError(friendly);
      emitAnalytics("add_to_cart_failure", {
        productGid: ctx.productGid,
        variantId: ctx.variantId,
      });
    } finally {
      setSubmitting(false);
    }
  }

  // 2026-07-02: one-tap add for the post-ATC "complete the set" card.
  // Reuses the EXACT member-line construction of the in-flow bundle path
  // (util/bundleMemberLine) with the group id stashed at add-to-cart, so
  // the member joins the same design group in the cart and in production.
  async function onQuickAddMember(m: BundleMember) {
    const stash = lastAtcRef.current;
    if (!stash || quickAddBusy) return;
    setQuickAddBusy(true);
    try {
      let variantNumericId: string | null = null;
      const pick = bundleMemberVariantPicks[m.id];
      if (pick && /^\d+$/.test(pick)) variantNumericId = pick;
      if (!variantNumericId && m.variantGid) {
        const tail = m.variantGid.split("/").pop();
        if (tail && /^\d+$/.test(tail)) variantNumericId = tail;
      }
      if (!variantNumericId && m.productHandle) {
        const j =
          bundleMemberProductData[m.id] ??
          (await fetchProductJson(m.productHandle));
        const first = j?.variants?.[0]?.id;
        if (first != null) variantNumericId = String(first);
      }
      if (!variantNumericId) return;
      await addItemsToCart([
        {
          id: Number(variantNumericId),
          quantity: m.quantity,
          properties: buildMemberCartProps(m, stash.groupId, stash.propagatedProps),
        },
      ]);
      setQuickAddedIds((prev) => new Set(prev).add(m.id));
      void refreshCart();
      emitAnalytics("upsell_picked", {
        productGid: ctx.productGid,
        productHandle: m.productHandle,
        upsellKind: "second_item_card",
        upsellId: m.id,
      });
      const rkCart = (window as unknown as { RKCart?: { open?: () => void } })
        .RKCart;
      rkCart?.open?.();
    } catch {
      // Non-fatal: the card stays usable for a retry.
    } finally {
      setQuickAddBusy(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────

  // Wave 3: the "complete the set" second-item card. Same eligibility as
  // before (post add-to-cart, the top unclaimed auto-paired member) and
  // the same handler + pricing path - only its POSITION moves: it now
  // renders inside the finish screen instead of the foot. null when not
  // eligible, so the finish screen shows no empty slot for it.
  const finishSecondItemCard: React.ReactNode = added
    ? (() => {
        const candidate =
          (config.bundleMembers ?? [])
            .filter(
              (m) =>
                m.optional &&
                !selectedBundleIds.has(m.id) &&
                !quickAddedIds.has(m.id),
            )
            .sort((a, b) => a.sortOrder - b.sortOrder)[0] ?? null;
        if (!candidate) return null;
        return (
          <SecondItemCard
            member={candidate}
            productData={bundleMemberProductData[candidate.id] ?? null}
            fallbackPriceCents={effectiveBundleMemberPriceCents(candidate.id)}
            discountPct={
              MULTIBUY_ENABLED
                ? assignCustomDiscounts({
                    primaryQualifies: false,
                    primaryPriceCents: null,
                    members: [
                      {
                        id: candidate.id,
                        flowKind: candidate.flowKind ?? null,
                        included: true,
                        priceCents: effectiveBundleMemberPriceCents(
                          candidate.id,
                        ),
                        quantity: 1,
                      },
                    ],
                    existingUnitPriceCents: existingCustomUnitPricesCents,
                  }).memberDiscountPct[candidate.id] ?? 0
                : 0
            }
            thresholds={config.thresholds}
            cartCents={cartSubtotalCents}
            pickedVariantId={bundleMemberVariantPicks[candidate.id] ?? null}
            onPickVariant={setBundleMemberVariantPick}
            busy={quickAddBusy}
            justAdded={false}
            onAdd={() => void onQuickAddMember(candidate)}
          />
        );
      })()
    : null;

  return (
    <div
      className="rk-pc-root"
      ref={rootRef}
      // Mirrors the mobile sticky bar's render condition so CSS can pad the
      // bottom of the flow while the bar is covering it. Without this the
      // fixed bar permanently hides the last ~90-125px of content on a
      // phone, including the real add-to-cart at the end of the flow.
      data-rk-bar={(draft !== null || spots.length > 0) && !added ? "1" : "0"}
    >
      {leadHelperKind && (
        <LeadHelper
          kind={leadHelperKind}
          workerUrl={ctx.workerUrl}
          productHandle={ctx.productHandle}
          variantTitle={ctx.garmentColourName ?? undefined}
          onClose={() => setLeadHelperKind(null)}
        />
      )}
      {/* Phase 68: free-digitisation promo banner. Shown at the top of
        * the customiser for the duration of the editor session whenever
        * the toggle on /app/settings is on. Strikethrough alone is too
        * subtle — this is the headline that tells the customer the
        * offer is on before they commit to designing. */}
      {/* Phase 83: ONE perks banner. Each applicable offer renders as a
          row inside a single strip - free digitisation (settings toggle)
          and/or the bundle multi-buy teaser (pet/kid products, before the
          bundle section appears). Merged into one banner so we never stack
          two separate promo strips, which reads as spammy. The bundle row
          hides once bundleSectionVisible so the in-bundle strip takes over
          - one savings message on screen at a time. */}
      {(() => {
        const showDigi = !!config.settings?.digitisationFreePromo;
        // Phase 89: the multi-buy applies to EVERY customiser product - the
        // primary always qualifies (the customiser only loads on products in
        // the discount collection), so a 2nd custom item always saves,
        // whether or not this product has a paired bundle member. Show the
        // awareness teaser on all of them so shoppers know to add more. The
        // !bundleSectionVisible gate still hands off to the in-bundle strip
        // once the customer finishes designing, so only one savings message
        // is ever on screen at a time.
        const showBundle = MULTIBUY_ENABLED && !bundleSectionVisible;
        if (!showDigi && !showBundle) return null;
        return (
          <div
            className={`rk-pc-promo-banner${showDigi && showBundle ? " is-multi" : ""}`}
            role="status"
            aria-live="polite"
          >
            {showDigi && (
              <span className="rk-pc-promo-banner__row">
                <span className="rk-pc-promo-banner__badge" aria-hidden="true">★</span>
                <span className="rk-pc-promo-banner__copy">
                  <strong>Free digitisation</strong> on every custom design for a
                  limited time.{" "}
                  <span className="rk-pc-promo-banner__save">Save $30.</span>
                </span>
              </span>
            )}
            {showBundle && (
              <span className="rk-pc-promo-banner__row">
                <span className="rk-pc-promo-banner__badge" aria-hidden="true">★</span>
                <span className="rk-pc-promo-banner__copy">
                  <strong>Bundle &amp; save</strong> - buy 1, get the 2nd 15% off,
                  3rd+ 20% off
                </span>
              </span>
            )}
          </div>
        );
      })()}
      {/* Pre-designer summary: how it works, turnaround, what's included
        * and care. Shown only before the customer opens the editor (no
        * saved spots, no draft) so it sets expectations without cluttering
        * the flow once they're underway.
        *
        * NOT on staged templates (Oscar, 2026-07-24): the generic
        * "spot -> design -> thread" 3-step explainer describes the
        * single-garment flow and contradicts a bundle whose numbered stages
        * (1. The hoodie, 2. The cap, 3. The socks) already ARE the steps. */}
      {!stages && spots.length === 0 && !editorVisible && (
        <PreDesignerSummary config={config} />
      )}
      {/* Live blank-canvas embroidery visualiser. Opt-in per template.
        * An inline collapsible panel at the top of the customiser — it
        * takes real layout space, so it never covers the options or the
        * page below. Reflects the spot being edited, else the most
        * recent saved spot. */}
      {config.product.visualPreview && (
        <div className={`rk-pc-pv-mount${pvReady ? " is-ready" : ""}`}>
          {/* The rail is the desktop sticky element — it (and the dock
            * inside) is bounded by the customiser height, so the flyout
            * can never scroll down into the section below. On narrow
            * screens the rail is display:contents (a no-op). */}
          <div className="rk-pc-pv-rail">
          <button
            type="button"
            className={`rk-pc-pv-tab${previewOpen ? " is-open" : ""}`}
            onClick={() =>
              setPreviewOpen((o) => {
                // A manual close is sticky: don't let the auto-open effect
                // re-open the dock after the customer has dismissed it.
                if (o) previewUserClosedRef.current = true;
                return !o;
              })
            }
            aria-expanded={previewOpen}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="3"
                y="5"
                width="18"
                height="14"
                rx="2.5"
                stroke="currentColor"
                strokeWidth="1.7"
              />
              <circle cx="8.6" cy="10" r="1.7" fill="currentColor" />
              <path
                d="M4 18l5-5 3.4 3.4L16.3 12 20 16.2"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="rk-pc-pv-tab__label">
              {previewOpen ? "Hide design preview" : "Visualise the design"}
            </span>
            <span className="rk-pc-pv-tab__chevron" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
          <div
            className={`rk-pc-pv-dock${previewOpen ? " is-open" : ""}`}
            aria-hidden={!previewOpen}
          >
            <div className="rk-pc-pv-dock__clip">
              {/* W4 phase 2: photo mode. When the ACTIVE placement (the
                * draft's, else the most recent saved spot's) has both an
                * admin photo AND a phase-1 calibration, the dock mounts
                * the composited on-garment preview instead of the
                * artboard. Presence of both = the feature flag;
                * uncalibrated placements keep PreviewCanvas exactly
                * as-is. */}
              {(() => {
                const previewSpot = draft ?? spots[spots.length - 1] ?? null;
                const previewPlacement = previewSpot?.placementSlug
                  ? config.placements.find(
                      (p) => p.slug === previewSpot.placementSlug,
                    ) ?? null
                  : null;
                const photoMode = !!(
                  previewPlacement?.imageUrl && previewPlacement?.calibration
                );
                const colourHex =
                  ctx.garmentColourHex ??
                  config.product.variantColours?.[ctx.variantId ?? ""] ??
                  null;
                // 2026-07-24 (Oscar): the "Remove background" tick confused
                // customers, so it is no longer offered. Passing undefined
                // keeps both previews' own gating hiding the control; the
                // removeBg plumbing stays for drafts that already have it
                // set (their preview still honours it).
                const onToggleRemoveBg = undefined;
                if (photoMode && previewPlacement) {
                  return (
                    <OnGarmentPreview
                      config={config}
                      spot={previewSpot}
                      placement={previewPlacement}
                      workerUrl={ctx.workerUrl}
                      garmentColourHex={colourHex}
                      garmentColourName={ctx.garmentColourName}
                      active={previewOpen}
                      onToggleRemoveBg={onToggleRemoveBg}
                      // Drag commits land on the draft only - saved
                      // spots reposition via Edit, same as every other
                      // spot property.
                      onPreviewOffsetChange={
                        draft
                          ? (o) =>
                              setDraft((d) =>
                                d ? { ...d, previewOffset: o } : d,
                              )
                          : undefined
                      }
                    />
                  );
                }
                return (
                  <PreviewCanvas
                    config={config}
                    spot={previewSpot}
                    workerUrl={ctx.workerUrl}
                    garmentColourHex={colourHex}
                    garmentColourName={ctx.garmentColourName}
                    active={previewOpen}
                    onToggleRemoveBg={onToggleRemoveBg}
                  />
                );
              })()}
            </div>
          </div>
          </div>
        </div>
      )}
      {variantSwitched && spots.length > 0 && (
        <div className="rk-pc-variant-switch-banner" role="status">
          <span className="rk-pc-variant-switch-banner__text">
            You switched size or colour. Your saved spots are still here
            and will stitch onto the new variant the same way.
          </span>
          <button
            type="button"
            className="rk-pc-variant-switch-banner__btn"
            onClick={() => setVariantSwitched(false)}
          >
            Got it
          </button>
        </div>
      )}
      {/* Phase 28: always mounted. The "From a past order" path doesn't
        * depend on cart content; the "From your cart" tile only renders
        * when reusableDesigns is non-empty.
        * 2026-07-22: hidden on staged (multi-garment) templates. Reusing one
        * past design makes no sense across three garments, and it was noise
        * on the one screen that most needed to be obvious. */}
      {!stages && (
      <ReuseSection
        config={config}
        workerUrl={ctx.workerUrl}
        designs={reusableDesigns}
        defaultEmail={ctx.customerEmail}
        deepLink={reuseDeepLink}
        onPick={pickReuse}
        onPickProof={pickProof}
        open={openSection === "reuse" && !editorVisible}
        onToggleSection={() => {
          // Wave 1: the editor auto-opens on load, and an open editor
          // pins the Reuse body shut (open requires !editorVisible). An
          // auto-opened draft the customer hasn't touched must not turn
          // the Reuse header into a dead click - drop the pristine draft
          // so the section can expand.
          if (pendingAutoStartRef.current && draft && editingSpotId === null) {
            pendingAutoStartRef.current = false;
            setDraft(null);
            setEditingSpotId(null);
          }
          toggleSection("reuse");
        }}
        onStartFresh={startNewSpot}
      />
      )}


      {/* Occasion urgency (2026-07-19): a real deadline converts the
          fence-sitters. Data-driven from occasions.ts; self-expires the
          day after the order-by date. Always visible, section open or
          not. */}
      {(() => {
        const occ = activeOccasion();
        if (!occ) return null;
        return (
          <div className="rk-pc-occasion">
            <span className="rk-pc-occasion__line">
              <strong>{occ.eventLabel}:</strong> order by{" "}
              <strong>{occ.orderByLabel}</strong> so it stitches and ships in
              time.
              <span className="rk-pc-occasion__chip">
                {occ.daysLeft} {occ.daysLeft === 1 ? "day" : "days"} left
              </span>
            </span>
            {occ.note && (
              <span className="rk-pc-occasion__note">{occ.note}</span>
            )}
          </div>
        );
      })()}

      <section
        ref={embroideryRef}
        className={`rk-pc-sec${editorVisible || spots.length > 0 ? " is-active" : ""}`}
      >
        <button
          type="button"
          className="rk-pc-sec__head"
          onClick={() => {
            if (!editorVisible) {
              toggleSection("customise");
              return;
            }
            // Wave 1 (dead-click killer): while the editor is up the
            // header can't collapse the section, so a tap glides back to
            // the step in progress instead of doing nothing.
            scrollToBlocking();
          }}
          aria-expanded={customiseOpen}
        >
          <span
            className={`rk-pc-sec__num${completedSections.has("customise") ? " is-done" : ""}`}
          >
            {completedSections.has("customise") ? "✓" : "1"}
          </span>
          <span className="rk-pc-sec__heading">
            <span className="rk-pc-sec__title">
              {flowCopy(config, "customiseTitle", "Customise your embroidery")}
            </span>
            <span className="rk-pc-sec__summary">{summary(spots, config)}</span>
          </span>
          {/* Wave 1: the price chip looks tappable, so it is - a tap
              opens the "What's in this price?" breakdown at the foot.
              stopPropagation keeps the header's own scroll/toggle from
              double-firing. */}
          {displayTotalCents > 0 ? (
            <span
              className="rk-pc-sec__price"
              onClick={(e) => {
                e.stopPropagation();
                openPriceBreakdown();
              }}
            >
              +${(displayTotalCents / 100).toFixed(0)}
            </span>
          ) : (
            <span
              className="rk-pc-sec__price is-free"
              onClick={(e) => {
                e.stopPropagation();
                openPriceBreakdown();
              }}
            >
              Free
            </span>
          )}
          {!editorVisible && (
            <span className="rk-pc-sec__chevron" aria-hidden>
              {customiseOpen ? "▾" : "▸"}
            </span>
          )}
        </button>
        <div className="rk-pc-collapse" data-open={customiseOpen}>
        <div className="rk-pc-collapse__clip">
        <div className="rk-pc-sec__body">
          {/* Multi-garment templates render one titled block per garment so
              it is obvious what is being customised and what is still
              outstanding. StarterStrip is deliberately NOT rendered here: its
              tiles seed a TEXT draft on the template's default placement,
              which on the Father's Day box is `centre` (logo only), so every
              tile produced an unusable draft. */}
          {stages ? (
            <div className="rk-pc-stages" ref={savedSpotsRef}>
              {liveStages.map((st) => {
                const mine = spotsForStage(st);
                return (
                  <div
                    className="rk-pc-stage"
                    key={st.key}
                    data-done={mine.length > 0 ? "1" : "0"}
                    data-stage-key={st.key}
                    // The next thing to do gets a static visual accent, so
                    // after a save the eye finds the next garment WITHOUT the
                    // page being scrolled for them. Layout, not motion.
                    data-next={st.key === unfinishedStage?.key ? "1" : "0"}
                  >
                    <div className="rk-pc-stage__head">
                      <span className="rk-pc-stage__title">{st.title}</span>
                      <span className="rk-pc-stage__state">
                        {mine.length > 0 ? "✓ done" : "to do"}
                      </span>
                    </div>
                    <p className="rk-pc-stage__hint">{st.hint}</p>
                    {/* A real Shopify option axis this garment owns (the
                        hoodie's colour). Drives the theme's own radio rather
                        than holding its own value, so price, stock and the
                        pre-order path can never disagree with the swatch. */}
                    {st.variantAxis && (
                      <VariantAxisPicker
                        axisName={st.variantAxis}
                        label={st.variantAxis}
                        productJson={productJson}
                        variantColours={config.product.variantColours}
                        currentVariantId={ctx.variantId}
                      />
                    )}
                    {/* This garment's own choices (e.g. which colour cap
                        goes in the box). Rendered INSIDE the stage, above
                        its spots, because every choice for a garment has to
                        live in that garment's block. Never render these
                        below the stage list. */}
                    {fieldsForStage(st).map((f) => (
                      <CustomFieldRenderer
                        key={f.id}
                        field={f}
                        value={fieldValues[f.slug]}
                        onChange={(v) => setFieldValue(f.slug, v)}
                      />
                    ))}
                    {mine.length > 0 && (
                      <div className="rk-pc-saved-spots">
                        {mine.map((s) => {
                          const idx = spots.indexOf(s);
                          return (
                            <SavedSpotTile
                              key={s.id}
                              spot={s}
                              index={idx}
                              config={config}
                              otherSpots={spots.slice(0, idx)}
                              onEdit={() => editSpot(s.id)}
                              onRemove={() => removeSpot(s.id)}
                            />
                          );
                        })}
                      </div>
                    )}
                    {editorStageKey === st.key && renderEditor()}
                    {!editorVisible &&
                      !(st.maxSpots != null && mine.length >= st.maxSpots) && (
                        <button
                          type="button"
                          className="rk-pc-add-spot-btn"
                          onClick={() => startStageSpot(st.key)}
                        >
                          {mine.length > 0
                            ? (st.ctaMore ?? "+ Add another spot")
                            : st.cta}
                        </button>
                      )}
                  </div>
                );
              })}
              {/* Placements no stage claims. Rendered last rather than hidden,
                  so adding a placement in the admin without updating
                  stages.ts degrades to "still reachable" instead of
                  "invisible". */}
              {stageExtras.length > 0 && (
                <div className="rk-pc-stage" data-done="0">
                  <div className="rk-pc-stage__head">
                    <span className="rk-pc-stage__title">Anything else</span>
                  </div>
                  {spots.filter(isUnstagedSpot).length > 0 && (
                    <div className="rk-pc-saved-spots">
                      {spots
                        .filter(isUnstagedSpot)
                        .map((s) => {
                          const idx = spots.indexOf(s);
                          return (
                            <SavedSpotTile
                              key={s.id}
                              spot={s}
                              index={idx}
                              config={config}
                              otherSpots={spots.slice(0, idx)}
                              onEdit={() => editSpot(s.id)}
                              onRemove={() => removeSpot(s.id)}
                            />
                          );
                        })}
                    </div>
                  )}
                  {!editorVisible && (
                    <button
                      type="button"
                      className="rk-pc-add-spot-btn"
                      onClick={startNewSpot}
                    >
                      + Add another spot
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              {!editorVisible && spots.length === 0 && (
                <>
                  <button
                    type="button"
                    className="rk-pc-add-spot-btn"
                    onClick={startNewSpot}
                  >
                    Start designing
                  </button>
                </>
              )}
              {spots.length > 0 && (
                <div className="rk-pc-saved-spots" ref={savedSpotsRef}>
                  {spots.map((s, idx) => (
                    <SavedSpotTile
                      key={s.id}
                      spot={s}
                      index={idx}
                      config={config}
                      otherSpots={spots.slice(0, idx)}
                      onEdit={() => editSpot(s.id)}
                      onRemove={() => removeSpot(s.id)}
                    />
                  ))}
                  {!editorVisible && (
                    <button
                      type="button"
                      className="rk-pc-add-spot-btn"
                      onClick={startNewSpot}
                    >
                      + Add another spot
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Phase 72: admin-defined "before_design" custom fields.
              Surfaced above the placement editor so intro questions
              (gift recipient, occasion, etc.) come first. */}
          <CustomFieldsSection
            config={config}
            placement="before_design"
            fieldValues={fieldValues}
            onChange={setFieldValue}
          />

          {/* Starter examples also render ABOVE the auto-opened editor
              while the draft is still pristine - the fresh-load auto-open
              means the pre-editor empty state (above) rarely shows. A tap
              replaces the pristine draft with the pre-filled one. */}
          {editorVisible &&
            spots.length === 0 &&
            !stages &&
            draft &&
            !draft.placementSlug &&
            !draft.mode &&
            !draft.text.trim() &&
            !draft.uploadStagingKey &&
            null}
          {/* Unstaged templates only; staged ones render it in-stage above. */}
          {!stages && renderEditor()}
        </div>
        </div>
        </div>
      </section>

      {/* Social proof (2026-07-19): real review photos of stitched
          orders, curated in examples.ts. Answers "will it look good?"
          while the customer designs. */}
      <ExamplesStrip />

      {/* Phase 33d: sleeve cross-sell. Only renders when the customer
        * has saved a non-sleeve spot + the template has a sleeve_name
        * upsell wired up. Clicking the CTA spawns a new draft with the
        * sleeve placement + any ticked emoji upsells stacked into the
        * draft's extraAddonIds. Kept just above the finish screen. */}
      {approvalSectionVisible && (
        <SleeveCrossSellSection
          config={config}
          spots={spots}
          draft={draft}
          onPickSleeve={(next) => {
            setEditingSpotId(null);
            setDraft(next);
          }}
        />
      )}

      {/* Phase 72: admin-defined "order_level" custom fields. Sit just
          above the finish screen's paid add-ons. */}
      {approvalSectionVisible && (
        <CustomFieldsSection
          config={config}
          placement="order_level"
          fieldValues={fieldValues}
          onChange={setFieldValue}
        />
      )}

      {/* Phase 72: admin-defined "after_design" custom fields (e.g.
          "delivery instructions", "is this a gift?"). Answered just
          before the finish screen. */}
      {approvalSectionVisible && (
        <CustomFieldsSection
          config={config}
          placement="after_design"
          fieldValues={fieldValues}
          onChange={setFieldValue}
        />
      )}

      {/* Wave 3: the consolidated "Finish your order" screen. Replaces the
          scattered approval / add-ons / back cross-sell / complete-the-set
          / notes steps + their Continue chain with one titled group.
          Approval leads (a free choice), then the paid add-ons, the back
          cross-sell (self-gated), the post-ATC complete-the-set card, and
          notes as a collapsed disclosure. Every handler + pricing path is
          the one the old sections used, so the cart properties are
          unchanged. */}
      {approvalSectionVisible && (
        <FinishSection
          config={config}
          approvalChoice={approvalChoice}
          onChooseApproval={(c) => {
            setApprovalChoice(c);
            setApprovalAddonId(null);
          }}
          approvalAddonId={approvalAddonId}
          onChooseApprovalAddon={(upsellId) => {
            setApprovalAddonId(upsellId);
            // Picking an approval-replacement upsell IS opting into
            // approval (with extras), so flip the choice gate as well.
            setApprovalChoice("approve");
          }}
          orderAddons={finishOrderAddons}
          orderAddOnIds={orderAddOnIds}
          onToggleOrderAddOn={toggleOrderAddOn}
          spots={spots}
          draft={draft}
          lockedSlugs={lockedSlugs}
          onPickBack={(next) => {
            setEditingSpotId(null);
            setDraft(next);
            emitAnalytics("upsell_picked", {
              productGid: ctx.productGid,
              productHandle: ctx.productHandle,
              variantId: ctx.variantId,
              upsellKind: "back_cross_sell",
              placementSlug: next.placementSlug,
            });
          }}
          secondItemCard={finishSecondItemCard}
          notesValue={customerNotes}
          onNotesChange={setCustomerNotes}
          analyticsBase={{
            productGid: ctx.productGid,
            productHandle: ctx.productHandle,
            variantId: ctx.variantId,
          }}
        />
      )}

      <div className="rk-pc-foot">
        {/* Phase 74 / 75: bundle preview as an interactive add-on
            section. Optional members render as opt-in checkboxes
            with their live price; mandatory members render as a
            "Included" tick row. No-op when the template has no
            bundle members.
            Phase 80: only render once the customer can actually add
            to cart (at least one finished spot + variant picked +
            approval choice made). Before that the customer is still
            mid-design and the bundle is just noise. */}
        {bundleSectionVisible && (
        <BundlePreviewSection
          config={config}
          selectedIds={selectedBundleIds}
          prices={bundleMemberPrices}
          // Phase 88: one unified discount map for every customiser member.
          discountByMemberId={customDiscounts.memberDiscountPct}
          productData={bundleMemberProductData}
          variantPicks={bundleMemberVariantPicks}
          expandedId={expandedBundleRowId}
          onPickVariant={(memberId, variantId) => {
            setBundleMemberVariantPick(memberId, variantId);
            // Collapse the row once the customer commits, matching
            // the "tick → expand → pick → collapse" flow.
            setExpandedBundleRowId(null);
          }}
          onToggleExpand={toggleExpandBundleRow}
          onToggle={toggleBundleMember}
        />
        )}
        {/* Phase 88: unified multi-buy explainer + total savings strip.
            Copy shifts based on what the customer's NEXT tick would
            unlock:
              - next item = position 2: "Buy 1, get the second 15% off"
              - next item = position 3: "The next one is 20% off"
              - next item = position 4+: "Another one? It's still 20% off"
            Renders whenever the bundle preview shows at least one
            qualifying member. Same canAddToCart gate as the bundle. */}
        {MULTIBUY_ENABLED && bundleSectionVisible && !firstUnpickedBundleMember && customDiscounts.totalSavingsCents === 0 && (config.bundleMembers ?? []).some((m) =>
          memberQualifies(m.flowKind),
        ) && (() => {
          // Count qualifying customiser units currently committed: cart +
          // primary (always qualifies on a customiser PDP) + every included
          // qualifying bundle member (mandatory always counts; optional
          // only when ticked).
          const bundleQualifying = (config.bundleMembers ?? []).reduce(
            (sum, m) => {
              if (!memberQualifies(m.flowKind)) return sum;
              const included = !m.optional || selectedBundleIds.has(m.id);
              if (!included) return sum;
              return sum + (m.quantity ?? 1);
            },
            0,
          );
          const totalCommitted =
            existingCustomCountInCart + 1 + bundleQualifying;
          const nextPosition = totalCommitted + 1;
          let copy: React.ReactNode;
          if (nextPosition <= 2) {
            copy = (
              <>
                <strong>Bundle and Save</strong> | Buy 1, get the
                second 15% off
              </>
            );
          } else if (nextPosition === 3) {
            copy = <>The next one is 20% off</>;
          } else {
            copy = <>Another one? It's still 20% off</>;
          }
          return (
            <div className="rk-pc-pet-savings rk-pc-pet-savings--explainer">
              <span className="rk-pc-pet-savings__icon" aria-hidden>★</span>
              <span className="rk-pc-pet-savings__label">{copy}</span>
            </div>
          );
        })()}
        {bundleSectionVisible && customDiscounts.totalSavingsCents > 0 && !firstUnpickedBundleMember && (
          <div className="rk-pc-pet-savings">
            <span className="rk-pc-pet-savings__icon" aria-hidden>★</span>
            <span className="rk-pc-pet-savings__label">
              You're saving{" "}
              <strong>{moneyFmt(customDiscounts.totalSavingsCents)}</strong>
              {" "}at checkout with the bundle
            </span>
          </div>
        )}
        {/* Phase 81c: blocker strip - sits directly above the ATC
            button when a ticked bundle member still needs a variant
            pick. Hides the savings strip so the customer isn't
            told two competing things at once. */}
        {firstUnpickedBundleMember && spots.length > 0 && (
          <div className="rk-pc-bundle-blocker">
            <span className="rk-pc-bundle-blocker__icon" aria-hidden>⚠</span>
            <span className="rk-pc-bundle-blocker__label">
              Pick a colour and size for{" "}
              <strong>
                {firstUnpickedBundleMember.productTitle ||
                  firstUnpickedBundleMember.productHandle}
              </strong>{" "}
              before adding to cart.
            </span>
          </div>
        )}
        {added ? (
          <div className="rk-pc-banner is-ok">
            Added to cart ✓{" "}
            <button
              type="button"
              className="rk-pc-banner__cart-link"
              onClick={() => {
                const rkCart = (
                  window as unknown as { RKCart?: { open?: () => void } }
                ).RKCart;
                if (rkCart?.open) rkCart.open();
                else window.location.href = "/cart";
              }}
            >
              View cart / checkout
            </button>
          </div>
        ) : err ? (
          <div className="rk-pc-banner is-error">{err}</div>
        ) : null}
        {/* 2026-07-02 → Wave 3: the second-item "complete the set" card now
            lives inside the finish screen (finishSecondItemCard), rendered
            above once added-to-cart makes it eligible. */}
        {/* Wave 1 trust strip: value + human-check reassurance right at
            the price area. Plain hyphen only, no em-dash. */}
        <div className="rk-pc-trust-line rk-pc-trust-line--digitising">
          Free digitising included (worth $30) - every design is checked by
          a human before we stitch
        </div>
        <PriceBreakdown
          spots={spots}
          config={config}
          approvalChoice={approvalChoice}
          approvalAddonId={approvalAddonId}
          orderAddOnIds={orderAddOnIds}
          garmentPriceCents={garmentPriceCents}
        />
        {(() => {
          // Phase 75: sum every bundle member that's actually getting
          // added (mandatory or ticked-optional). Members whose price
          // hasn't loaded yet contribute 0 (defensive).
          // Phase 81: when the customer has picked a variant, look up
          // THAT variant's price (variants can differ in price across
          // colours/sizes); else fall back to bundleMemberPrices.
          const bundleAddCents = (config.bundleMembers ?? []).reduce(
            (sum, m) => {
              const included = !m.optional || selectedBundleIds.has(m.id);
              if (!included) return sum;
              const price = effectiveBundleMemberPriceCents(m.id);
              if (typeof price !== "number") return sum;
              return sum + price * (m.quantity ?? 1);
            },
            0,
          );
          // Phase 88: discount the item total by the savings the unified
          // Shopify Function will apply at checkout. No-op until there are
          // 2+ qualifying units (totalSavingsCents === 0).
          const itemTotalCents =
            (garmentPriceCents ?? 0) +
            displayTotalCents +
            bundleAddCents -
            customDiscounts.totalSavingsCents;
          // Threshold unlock line: project "cart + this item" against the
          // storewide value unlocks ($90 free AU shipping, $250 free socks;
          // rows come from config.thresholds and are kept matched with the
          // real delivery profile / gift enforcement). Shows the next unmet
          // milestone, or the earned-it state once both are cleared.
          const unlockables = (config.thresholds ?? [])
            .slice()
            .sort((a, b) => a.amountCents - b.amountCents);
          const projectedCents =
            cartSubtotalCents + Math.max(itemTotalCents, 0);
          const nextUnlock = unlockables.find(
            (t) => projectedCents < t.amountCents,
          );
          const unlockRow =
            unlockables.length === 0 || garmentPriceCents == null ? null : nextUnlock ? (
              <div className="rk-pc-unlock-row">
                You&apos;re {moneyFmt(nextUnlock.amountCents - projectedCents)}{" "}
                from {nextUnlock.label}
              </div>
            ) : (
              <div className="rk-pc-unlock-row rk-pc-unlock-row--done">
                ✓ {unlockables.map((t) => t.label).join(" + ")} included with
                this order
              </div>
            );
          return (
            <>
              <div className="rk-pc-total-row">
                <span className="rk-pc-total-row__label">
                  {garmentPriceCents != null ? "Item total" : "Embroidery total"}
                </span>
                <span className="rk-pc-total-row__value">
                  {garmentPriceCents != null
                    ? moneyFmt(itemTotalCents)
                    : displayTotalCents === 0
                      ? "Free"
                      : `+$${(displayTotalCents / 100).toFixed(0)}`}
                </span>
              </div>
              {unlockRow}
            </>
          );
        })()}
        {KLAVIYO_RECOVERY_ENABLED && spots.length > 0 && !ctx.customerEmail && !added && !editorVisible && (
          <GuestSaveDesign
            email={guestEmail}
            consent={guestConsent}
            saved={guestSaved}
            onEmail={setGuestEmail}
            onConsent={setGuestConsent}
            onSave={() => {
              // The design token normally already exists (minted at the
              // first spot save, and the card only renders with spots);
              // minting here covers legacy persisted sessions from before
              // tokens moved to spot save.
              const gid = designGroupId ?? mintGroupId();
              const hadToken = designGroupId != null;
              if (!hadToken) setDesignGroupId(gid);
              fireDesignStarted(guestEmail, undefined, gid);
              // Server draft save at the consent moment - this is what
              // makes "we've emailed you a link so you can finish this
              // design" true. When the token was only just minted, the
              // flush rides the persist effect that the setDesignGroupId
              // state change triggers (the envelope has to carry the
              // token before the POST goes out).
              armDraftSync(guestEmail, "guest_card", hadToken);
              setGuestSaved(true);
            }}
          />
        )}
        {/* 2026-07-03: sold-out back-order note at the commit point, so the
            customer knows it ships when the size is restocked BEFORE they
            add - no surprise at checkout. 2026-07-25: full price, no discount. */}
        {currentPreorder.active && !added ? (
          <div className="rk-pc-preorder-note">
            <strong>{currentPreorder.sizeLabel} is sold out right now.</strong>{" "}
            You can still order it, it ships when the size is back{" "}
            {currentPreorder.etaText ? (
              <>(expected <strong>{currentPreorder.etaText}</strong>)</>
            ) : (
              <>({PREORDER_ETA_COPY})</>
            )}
            . Your design is saved to it.
          </div>
        ) : (
          /* 2026-07-02: reassurance at the commit point. */
          <div className="rk-pc-trust-line">
            <span aria-hidden>★★★★★</span> 2,000+ five-star reviews &middot; if
            the stitching isn&apos;t right, we re-stitch it &middot; Melbourne
            studio
          </div>
        )}
        {atcError && !added ? (
          <div className="rk-pc-atc-error" role="alert">
            {atcError}
          </div>
        ) : null}
        <button
          type="button"
          className={`rk-pc-cta${currentPreorder.active && !added ? " is-preorder" : ""}${!added && !canAddToCart ? " is-blocked" : ""}`}
          // Once added, the button starts a brand-new design rather than
          // re-adding the same one to the cart.
          //
          // Wave 1 (dead-click killer): while blocked the button keeps
          // its disabled look (is-blocked) but stays clickable as a
          // GUIDE - a tap scrolls to the blocking step and pulses the
          // "Still to do" list instead of dying. It can never submit
          // while blocked (guideBlockedAtc doesn't call onAddToCart, and
          // onAddToCart re-checks canAddToCart anyway). The native
          // disabled attr only remains for the in-flight submit.
          aria-disabled={!added && !canAddToCart ? true : undefined}
          onClick={
            added ? startOver : canAddToCart ? onAddToCart : guideBlockedAtc
          }
          disabled={!added && submitting}
        >
          {submitting
            ? "Adding…"
            : added
              ? "Start a new design"
              : !ctx.variantId
                ? "Pick a size first"
                : spots.length === 0
                  ? editorVisible
                    ? "Finish your spot above"
                    : "Add a spot to continue"
                  : editorBlock.label
                    ? editorBlock.label
                  : approvalChoice === null
                    ? "Pick approval option above"
                    : firstUnpickedBundleMember
                      ? `Pick options for ${firstUnpickedBundleMember.productTitle || firstUnpickedBundleMember.productHandle}`
                      : // Both of these gate canAddToCart above. Without a
                        // branch here the button reads "Add to cart" while
                        // refusing to add, which is what it did on the
                        // Father's Day box from the day stages shipped.
                        unfinishedStage
                        ? `Finish ${stageLabel(unfinishedStage)}`
                        : firstMissingStageField
                          ? `Pick the ${firstMissingStageField.field.label.toLowerCase()}`
                          : currentPreorder.active
                        ? "Back-order it, ships when restocked"
                        : atcItemCount > 1
                          ? `Add ${atcItemCount} items to cart`
                          : "Add to cart"}
        </button>
      </div>
      <WorkflowFooter />
      {/* 2026-07-02: mobile sticky total (<=640px, CSS-gated). Mid-flow on a
          phone, the running total and the ATC both sit off-screen while fees
          accumulate - this keeps the live price visible and gives a one-tap
          jump back to the commit point. Hidden once added to cart.
          2026-07-24: PORTALLED to document.body - the theme wraps every
          section's content in a z-10 stacking context, so a fixed bar
          rendered inside the customiser was painted over by any LATER
          section (the Loox reviews, in Oscar's screenshot). At body level
          its own z-index competes at the root and wins. */}
      {(draft !== null || spots.length > 0) && !added && ReactDOM.createPortal(
        <div className={`rk-pc-mobile-bar${mobileBarHidden ? " is-suppressed" : ""}`}>
          {/* Wave 1 trust strip, mobile flavour: rides the sticky bar so
              it's on screen while the editor is open without scrolling. */}
          <div className="rk-pc-mobile-bar__trust">
            Free digitising included (worth $30) - every design is checked
            by a human before we stitch
          </div>
          <div className="rk-pc-mobile-bar__price">
            <span className="rk-pc-mobile-bar__label">
              {garmentPriceCents != null ? "Item total" : "Embroidery"}
            </span>
            <span className="rk-pc-mobile-bar__value">
              {(() => {
                const bundleAdd = (config.bundleMembers ?? []).reduce(
                  (sum, m) =>
                    !m.optional || selectedBundleIds.has(m.id)
                      ? sum + (effectiveBundleMemberPriceCents(m.id) ?? 0)
                      : sum,
                  0,
                );
                return moneyFmt(
                  (garmentPriceCents ?? 0) +
                    displayTotalCents +
                    bundleAdd -
                    customDiscounts.totalSavingsCents,
                );
              })()}
            </span>
          </div>
          <button
            type="button"
            className="rk-pc-mobile-bar__btn"
            onClick={() => {
              // Wave 1: mid-draft the disabled ATC is a dead end, so the
              // bar sends the customer back to the step in progress
              // instead. Once everything's ready it jumps to the commit
              // point as before.
              if (!canAddToCart) {
                scrollToBlocking();
                return;
              }
              const el = rootRef.current?.querySelector(".rk-pc-cta");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
          >
            {canAddToCart ? (
              <>Review &amp; add ↓</>
            ) : editorBlock.label ? (
              <>Save your spot ↑</>
            ) : (
              <>Continue designing ↑</>
            )}
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

function isValidEmail(e: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim());
}

// Abandoned-design recovery card. Shown only to guests (logged-in shoppers
// already have an email on file) once they have a real design in progress,
// just above the add-to-cart button. Captures an opt-in email so we can
// email them a link back to finish later. Nothing is sent unless they enter
// a valid email, tick consent, and click.
function GuestSaveDesign(props: {
  email: string;
  consent: boolean;
  saved: boolean;
  onEmail: (v: string) => void;
  onConsent: (v: boolean) => void;
  onSave: () => void;
}) {
  const { email, consent, saved, onEmail, onConsent, onSave } = props;
  if (saved) {
    return (
      <div className="rk-pc-savedesign rk-pc-savedesign--done" role="status">
        <strong>saved, check your inbox.</strong> we've emailed you a link so
        you can finish this design whenever you like, no need to start over.
      </div>
    );
  }
  const ready = isValidEmail(email) && consent;
  return (
    <div className="rk-pc-savedesign">
      <div className="rk-pc-savedesign__head">not ready to checkout?</div>
      <p className="rk-pc-savedesign__copy">
        pop your email in and we'll send you a link so you can finish this
        design later, no need to start over.
      </p>
      <div className="rk-pc-savedesign__row">
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          className="rk-pc-savedesign__input"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => onEmail(e.target.value)}
        />
        <button
          type="button"
          className="rk-pc-savedesign__btn"
          disabled={!ready}
          onClick={onSave}
        >
          email me my link
        </button>
      </div>
      <label className="rk-pc-savedesign__consent">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => onConsent(e.target.checked)}
        />
        <span>yep, email me my design link and the occasional offer.</span>
      </label>
    </div>
  );
}

// Format a cents amount as a dollar string — drops the decimals when the
// amount is a whole number of dollars ($178), keeps them otherwise
// ($177.95).
function moneyFmt(cents: number): string {
  const d = cents / 100;
  return Number.isInteger(d) ? `$${d}` : `$${d.toFixed(2)}`;
}

function summary(spots: Spot[], config: Config): string {
  if (spots.length === 0) return "Pick a placement to start";
  if (spots.length === 1) {
    const s = spots[0];
    const p = config.placements.find((pp) => pp.slug === s.placementSlug);
    return `${p?.name ?? s.placementSlug} · ${s.sizeMm}mm`;
  }
  return `${spots.length} spots configured`;
}

// Inject @font-face for every embroidery font in the config so font tile
// previews actually render in the chosen face. Idempotent.
function injectFontFaces(fonts: Config["fonts"]) {
  if (typeof document === "undefined") return;
  const styleId = "rk-pc-fonts";
  let style = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = styleId;
    document.head.appendChild(style);
  }
  style.textContent = fonts
    .filter((f) => f.fileUrl)
    .map(
      (f) =>
        `@font-face{font-family:'${f.family}';src:url('${f.fileUrl}') format('woff2');font-display:swap;}`,
    )
    .join("");
}
